import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup} from "@angular/forms";
import * as moment from "moment";
import firebase from "firebase";
import 'jspdf-autotable';
import {UserService} from "../../../../services/user.service";
import {User, UserLogs} from "../../../../models/user.model";
import {VehicleService} from "../../../../services/vehicle.service";
import Swal from "sweetalert2";
import {SwalComponent, SwalPortalTargets} from "@sweetalert2/ngx-sweetalert2";
import {AngularFirestore} from "@angular/fire/firestore";
import {DatePipe} from "@angular/common";
import {Account} from "../../../../models/account.model";
import {AuthService} from "../../../../services/auth.service";
import {AccountService} from 'src/services/account.service';
import {TemplateOccupancyTimeHeader, TemplateOccupancyTimeRage} from 'src/app/constant/template-occupancy-time-list';
import {MonthsOfYear, Years} from 'src/app/constant/months-of-year';
import {LogsService} from 'src/services/log.service';
import {SendEmailsService} from "../../../../services/send-emails.service";
import {AngularFireStorage} from '@angular/fire/storage';
// Import Excel and Word export libraries
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import * as docx from 'docx';
// Import the PDF image service
import { PdfImageService } from '../../../../services/pdf-image.service';
import { AuditService } from '../../../../services/audit.service';
import { AuditActionType } from '../../../../models/audit.model';
import { ZoneSlotAssignment } from '../../../../enum/enum';


@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styles: [`
    .column-selector-btn {
      border: 1px solid #d1d5db;
      background: white;
      color: #374151;
      padding: 0.5rem 0.75rem;
      border-radius: 0.25rem;
      font-size: 0.875rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      transition: background-color 0.15s;
    }
    
    .column-selector-btn:hover {
      background-color: #f9fafb;
    }
    
    .column-dropdown {
      position: absolute;
      left: 0;
      margin-top: 0.25rem;
      background: white;
      border: 1px solid #d1d5db;
      border-radius: 0.25rem;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
      z-index: 50;
      width: 280px;
    }
    
    .column-dropdown-header {
      padding: 0.5rem;
      border-bottom: 1px solid #e5e7eb;
      background-color: #f9fafb;
      display: flex;
      gap: 0.5rem;
    }
    
    .column-dropdown-btn {
      flex: 1;
      font-size: 0.75rem;
      background: white;
      border: 1px solid #d1d5db;
      color: #374151;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      cursor: pointer;
    }
    
    .column-dropdown-btn:hover {
      background-color: #f3f4f6;
    }
    
    .column-list {
      overflow-y: auto;
      max-height: 300px;
    }
    
    .column-item {
      padding: 0.5rem 0.75rem;
      cursor: pointer;
      border-bottom: 1px solid #f3f4f6;
    }
    
    .column-item:last-child {
      border-bottom: none;
    }
    
    .column-item:hover {
      background-color: #f9fafb;
    }
    
    .column-item label {
      display: flex;
      align-items: center;
      cursor: pointer;
      user-select: none;
    }
    
    .column-item input[type="checkbox"] {
      margin-right: 0.5rem;
    }
    
    .column-item span {
      font-size: 0.875rem;
    }
  `],
  // Using Default change detection strategy for better debugging
  // Will switch back to OnPush after fixing issues
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserReportsComponent implements OnInit {

  // Table display properties
  public showResultsTable: boolean = false;
  public tableData: any[] = [];
  public tableHeaders: string[] = [];
  public reportPeriod: string = '';
  public totalTransactions: number = 0;
  public totalAmount: number = 0;
  public totalRefundedAmount: number = 0;
  public totalCanceledCount: number = 0;
  public netAmount: number = 0;
  public totalNetAmount: number = 0; // Actual money collected based on amount_paid
  public totalUnpaidAmount: number = 0; // Total unpaid amount
  public totalUnpaidCount: number = 0; // Total unpaid transactions count
  public totalCancelledAmount: number = 0; // Total amount from cancelled transactions
  public employeeSummary: any[] = [];
  
  // Specialized report properties
  public summaryHeaders: string[] = []; // Headers for dsa and dstt reports
  public occupancyDates: string[] = []; // Dates for apo report
  
  // Pagination properties
  public currentPage: number = 1;
  public itemsPerPage: number = 10;
  public get totalPages(): number {
    return Math.ceil(this.getFilteredData().length / this.itemsPerPage);
  }
  
  // Sorting and filtering properties
  public sortColumn: string = '';
  public sortDirection: 'asc' | 'desc' = 'asc';
  public filterValue: string = '';
  public filterColumn: string = '';
  
  // Column visibility properties
  public allColumns: string[] = [];
  public visibleColumns: Set<string> = new Set();
  public defaultVisibleColumns: string[] = [
    'Date', 'Plate Number', 'Zone Name', 'Vehicle Type', 
    'Start', 'End', 'Status', 'Duration', 
    'Initial Rate', 'Total Payment', 'Net Amount'
  ];
  public showColumnSelector: boolean = false;
  
  
  /**
   * Apply filter to data
   */
  applyFilter(column: string, value: string) {
    this.filterColumn = column;
    this.filterValue = value;
    
    // Reset to first page when filtering
    this.currentPage = 1;
  }
  
  /**
   * Clear all filters
   */
  clearFilters() {
    this.filterColumn = '';
    this.filterValue = '';
    this.currentPage = 1;
  }
  
  /**
   * Get filtered and sorted data
   */
  getFilteredData(): any[] {
    let filteredData = [...this.tableData];
    
    // Apply filtering
    if (this.filterColumn && this.filterValue) {
      filteredData = filteredData.filter(item => {
        const value = item[this.filterColumn];
        if (value === undefined || value === null) return false;
        
        return value.toString().toLowerCase().includes(this.filterValue.toLowerCase());
      });
    }
    
    // Apply sorting
    if (this.sortColumn) {
      filteredData.sort((a, b) => {
        const valueA = a[this.sortColumn];
        const valueB = b[this.sortColumn];
        
        // Handle null/undefined values
        if (valueA === undefined || valueA === null) return this.sortDirection === 'asc' ? -1 : 1;
        if (valueB === undefined || valueB === null) return this.sortDirection === 'asc' ? 1 : -1;
        
        // Handle numeric values
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return this.sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
        }
        
        // Handle date values (check if it looks like a date string)
        if (valueA && valueB && 
            (valueA.toString().includes('/') || valueA.toString().includes('-')) && 
            (valueB.toString().includes('/') || valueB.toString().includes('-'))) {
          const dateA = new Date(valueA);
          const dateB = new Date(valueB);
          
          if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
            return this.sortDirection === 'asc' ? 
              dateA.getTime() - dateB.getTime() : 
              dateB.getTime() - dateA.getTime();
          }
        }
        
        // Handle string values
        const stringA = valueA.toString().toLowerCase();
        const stringB = valueB.toString().toLowerCase();
        
        if (stringA < stringB) return this.sortDirection === 'asc' ? -1 : 1;
        if (stringA > stringB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filteredData;
  }
  
  /**
   * Get current page data
   */
  getCurrentPageData(): any[] {
    const filteredData = this.getFilteredData();
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return filteredData.slice(startIndex, startIndex + this.itemsPerPage);
  }

  get color(): string {
    return this._color;
  }
  set color(color: string) {
    this._color = color !== "light" && color !== "dark" ? "light" : color;
  }
  private _color = "light";

  @ViewChild('tableContent')

  public readonly tableContent!: SwalComponent;

  // Property declarations removed to avoid duplication

  public dateTimeRangeLogs: Date[];
  public dateTimeRangeErrorLogs: Date[];
  dateRange = new Date();
  timeInData = [];
  employees: any = [];
  filteredByEmployee = [];
  form: FormGroup;
  user: UserLogs = <UserLogs>{};

  account: Account;
  userAuth: User;
  allEmployees:any = [];


  reportsGeneratedCounts: number = 0;

  headerRowTable = [['No.', 'Plate Number', 'Start', 'End']];

  employeeSelected = 'all';
  totalPaymentCollected = 0;

  selectedZone:any;
  zones: any;

  selectedReportType:any;

  vehicleTypes: any = [];



  weeklyDateRanges: { start: string; end: string }[] = [];

  hideDateRage = true;
  selectedWeeklyRange : string = '';
  selectedMonth: string = ''
  selectedYear:string = '';
  
  activeQuickDateRange: string = '';
  private isSettingQuickRange: boolean = false;

  monthsOfYear = MonthsOfYear;
  years = Years;

  minDate: Date;
  maxDate: Date;
  currentMonth = this.dateRange.getMonth();
  currentYear = this.dateRange.getFullYear();


  

  constructor(
    public formBuilder: FormBuilder,
    private userService: UserService,
    private vehicleService: VehicleService,
    public readonly swalTargets: SwalPortalTargets,
    private firestore: AngularFirestore,
    private datePipe: DatePipe,
    public auth: AuthService,
    private afs: AngularFirestore,
    private accountService: AccountService,
    private logsService : LogsService,
    private sendEmailsService: SendEmailsService,
    private storage: AngularFireStorage,
    private cdr: ChangeDetectorRef,
    private pdfImageService: PdfImageService, // Add the new service
    private auditService: AuditService
  ) {

    const now = new Date();
    // this.minDate = new Date(now.getFullYear(), now.getMonth(), 1);
    // this.maxDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    this.account = JSON.parse(localStorage.getItem('account'));

    this.zones = this.account.settings.zones;

    this.user = JSON.parse(localStorage.getItem('user'));

    this.auth.user$.subscribe(async user => {
      this.userAuth = user;

      if(this.userAuth.roles.superAdmin){
        if(this.account.settings.zones.length > 0){
          for(let zone of this.account.settings.zones){

            if(zone.employees.length > 0){
              let index = zone.employees.findIndex(e => e.employee_id == 'all')
              if(index == -1){
                this.allEmployees.push(
                  { date_created : null,
                    employee_id: 'all',
                    first_name: 'All',
                    last_name: '',
                    position: ''
                  }
                )
              }
              for(let employee of zone.employees){
                this.allEmployees.push(employee);
              }

              if (this.form) {
                this.form.patchValue({
                  employee : 'all'
                })
              }

            }
          }
        }
      }
      //await this.getMonitoringEmailLogs();
    });


    this.vehicleService.getVehicleTypes().then((vehicleTypes) => {
      vehicleTypes.forEach(vehicle => {
        this.vehicleTypes.push(vehicle.vehicle_type_name);
      });

    });

    // TODO: Need to add check if the month report is generated.
    for(let month of this.monthsOfYear){
      if(this.currentMonth == month.id){
        let lastDate = this.getLastDateOfMonth(this.currentYear,this.currentMonth);
        if(lastDate == this.dateRange.getDate()){
          month.enabled  = true;
        }else{
          month.enabled = false;
        }
      }else{
        month.enabled = false;
      }

      if(this.currentMonth > month.id){
        month.enabled = true
      }else{
        month.enabled = false;
      }
    }

  }

  // New Methods for Table Display and Export

  /**
   * Gets the keys of an object for dynamic table cell rendering
   */
  getObjectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  /**
   * Formats cell values for display in table
   */
  formatCellValue(value: any): string {
    if (value === undefined || value === null) return '';
    
    if (typeof value === 'number') {
      // Format currency values
      if (this.isCurrencyField(value)) {
        return '₱' + value.toFixed(2);
      }
      return value.toString();
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return value.toString();
  }
  
  /**
   * Determines if a field should be formatted as currency
   */
  isCurrencyField(value: any): boolean {
    // Check if property name contains keywords related to currency
    const currencyKeywords = ['amount', 'payment', 'price', 'fee', 'total', 'sales'];
    return currencyKeywords.some(keyword => value.toString().toLowerCase().includes(keyword));
  }

  /**
   * Validate date range to ensure it doesn't exceed 1 month
   */
  validateDateRange(startDate: Date, endDate: Date): { isValid: boolean; message: string } {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Check if the range is more than 31 days (approximately 1 month)
    if (diffDays > 31) {
      return {
        isValid: false,
        message: `Date range is too long (${diffDays} days). Please select a range of maximum 1 month (31 days).<br><br>
                  <strong>Supported ranges:</strong><br>
                  • Per day: Same start and end date<br>
                  • Per week: Maximum 7 days<br>
                  • Per month: Maximum 31 days`
      };
    }
    
    return { isValid: true, message: '' };
  }

  /**
   * Set quick date range shortcuts
   */
  setQuickDateRange(range: string) {

    this.isSettingQuickRange = true;
    this.activeQuickDateRange = range;

    const today = moment();
    let startDate: moment.Moment;
    let endDate: moment.Moment;

    switch (range) {
      case 'today':
        startDate = today.clone();
        endDate = today.clone();
        break;
      case 'yesterday':
        startDate = today.clone().subtract(1, 'day');
        endDate = today.clone().subtract(1, 'day');
        break;
      case 'thisWeek':
        startDate = today.clone().startOf('week');
        endDate = today.clone().endOf('week');
        break;
      case 'lastWeek':
        startDate = today.clone().subtract(1, 'week').startOf('week');
        endDate = today.clone().subtract(1, 'week').endOf('week');
        break;
      case 'thisMonth':
        startDate = today.clone().startOf('month');
        endDate = today.clone().endOf('month');
        break;
      default:
        this.isSettingQuickRange = false;
        return;
    }
    
    // Set flag to prevent clearing active state during programmatic update
    this.isSettingQuickRange = true;
    
    // Set active button
    this.activeQuickDateRange = range;
    console.log('Active quick date range set to:', this.activeQuickDateRange); // Debug log
    
    // Update form controls
    this.form.patchValue({
      dateRangeLogsFrom: startDate,
      dateRangeLogsTo: endDate
    });
    
    // Reset flag after update
    setTimeout(() => {
      this.isSettingQuickRange = false;
    }, 50);
    
    // Force change detection
    this.cdr.detectChanges();
  }

  /**
   * Clear active quick date range when dates are manually changed
   */
  onDateInputChange() {
    if (!this.isSettingQuickRange) {
      console.log('Date input changed, clearing active state'); // Debug log
      this.activeQuickDateRange = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Fetch data based on filters and display in table
   */
  async fetchAndDisplayData() {
    
    // Reset previous data
    this.showResultsTable = false;
    this.tableData = [];
    this.totalTransactions = 0;
    this.totalAmount = 0;
    this.totalNetAmount = 0;
    this.totalUnpaidAmount = 0;
    this.totalUnpaidCount = 0;
    this.totalCancelledAmount = 0;
    this.totalRefundedAmount = 0;
    this.totalCanceledCount = 0;
    this.employeeSummary = [];
    this.timeInData = [];
    
    // Validate inputs
    if (this.selectedReportType !== 'general') {
      if (this.zones.length === 1) {
        this.form.value.zone = this.zones[0].zone_name;
      }

      if (this.form.value.employee == '' || this.form.value.reportType == '' || this.form.value.zone == '') {
        await Swal.fire({
          title: 'Ooops!',
          html: 'Please select employee, zone and report type - these are required fields.',
          allowEscapeKey: true,
          allowOutsideClick: true,
          showConfirmButton: false,
          showCancelButton: true,
          showCloseButton: true,
          cancelButtonText: 'Ok',
          icon: 'warning',
        });
        return;
      }
    }

    // Validate date range
    const startDate = this.form.value.dateRangeLogsFrom;
    const endDate = this.form.value.dateRangeLogsTo;
    
    if (!startDate || !endDate) {
      await Swal.fire({
        title: 'Ooops!',
        html: 'Please select both start date and end date.',
        allowEscapeKey: true,
        allowOutsideClick: true,
        showConfirmButton: false,
        showCancelButton: true,
        showCloseButton: true,
        cancelButtonText: 'Ok',
        icon: 'warning',
      });
      return;
    }

    // Validate date order
    if (new Date(startDate) > new Date(endDate)) {
      await Swal.fire({
        title: 'Ooops!',
        html: 'Start date cannot be later than end date.',
        allowEscapeKey: true,
        allowOutsideClick: true,
        showConfirmButton: false,
        showCancelButton: true,
        showCloseButton: true,
        cancelButtonText: 'Ok',
        icon: 'warning',
      });
      return;
    }

    // Validate date range - maximum 1 month
    const dateRangeValidation = this.validateDateRange(new Date(startDate), new Date(endDate));
    if (!dateRangeValidation.isValid) {
      await Swal.fire({
        title: 'Date Range Too Long!',
        html: dateRangeValidation.message,
        allowEscapeKey: true,
        allowOutsideClick: true,
        showConfirmButton: false,
        showCancelButton: true,
        showCloseButton: true,
        cancelButtonText: 'Ok',
        icon: 'warning',
      });
      return;
    }

    if (this.employeeSelected !== '') {
      this.form.value.employee = this.employeeSelected;
    }

    // Process the date range
    if (this.form.valid) {
      const dateFrom = startDate;
      const dateTo = endDate;
      
      // Set report period display
      const startDateObj = new Date(dateFrom);
      const endDateObj = new Date(dateTo);
      
      if (startDateObj.toDateString() === endDateObj.toDateString()) {
        this.reportPeriod = startDateObj.toLocaleDateString();
      } else {
        this.reportPeriod = `${startDateObj.toLocaleDateString()} to ${endDateObj.toLocaleDateString()}`;
      }

      // Show loading indicator
      Swal.fire({
        title: 'Loading data...',
        allowEscapeKey: false,
        allowOutsideClick: false,
        showConfirmButton: false,
        showCancelButton: false,
        showCloseButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        // Set up date objects for Firestore query
        let start, end;
        
        try {
          start = new Date(dateFrom);
          end = new Date(dateTo);
          
          // Ensure we capture the full day range
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
          
          console.log('Date range for query:', { dateFrom, dateTo, start, end });
        } catch (error) {
          console.error('Error parsing dates:', error);
          Swal.close();
          await Swal.fire({
            title: 'Error',
            text: 'Invalid date range. Please check your date selection.',
            icon: 'error'
          });
          return;
        }
        
        let logs;
        
        // Query logs based on selection and zone
        // If form.value.zone is set, we should filter by that zone regardless of report type
        const selectedZoneName = this.form?.value?.zone;
        const hasZoneFilter = selectedZoneName && selectedZoneName !== '';
        
        // Find the selected zone object if a zone name is specified
        let zoneForQuery = this.selectedZone;
        if (hasZoneFilter) {
          zoneForQuery = this.zones.find(z => z.zone_name.toLowerCase() === selectedZoneName.toLowerCase());
          if (!zoneForQuery) {
            zoneForQuery = this.selectedZone; // Fall back to any previously selected zone
          }
        }
        
        try {
          if (hasZoneFilter && zoneForQuery) {
            
            logs = await firebase.firestore().collection('logs')
              .where('account_key', '==', this.account.account_key)
              .where('zone_id', '==', zoneForQuery.zone_id)
              .where('time_in', '>=', start)
              .where('time_in', '<=', end)
              .get();
          } else if (this.selectedReportType === 'general') {
            
            logs = await firebase.firestore().collection('logs')
              .where('account_key', '==', this.account.account_key)
              .where('time_in', '>=', start)
              .where('time_in', '<=', end)
              .get();
          } else {
            // Non-general report but no zone selected yet
            // Make sure selectedZone is defined for non-general reports
            if (!this.selectedZone) {
              if (this.zones && this.zones.length > 0) {
                this.selectedZone = this.zones[0];
              } else {
                await Swal.fire({
                  title: 'Error',
                  text: 'No zones available. Please select a zone first.',
                  icon: 'error'
                });
                return;
              }
            }
            
            logs = await firebase.firestore().collection('logs')
              .where('account_key', '==', this.account.account_key)
              .where('zone_id', '==', this.selectedZone.zone_id)
              .where('time_in', '>=', start)
              .where('time_in', '<=', end)
              .get();
          }
          
          console.log('Query completed, docs:', logs);
        } catch (error) {
          console.error('Firestore query error:', error);
          throw error;
        }
        
        if (logs.empty) {
          Swal.close(); // Close the loading dialog first
          await Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: 'No data found for the selected date range.'
          });
          return;
        }
        
        let processedData;
        
        // Process the data based on the report type
        switch (this.selectedReportType) {
          case 'general':
            // Process for general reports
            processedData = await this.processGeneralReport(logs);
            break;
            
          case 'dsa': // Summary per Attendant
            processedData = await this.processAttendantSummaryReport(logs);
            break;
            
          case 'dstt': // Summary per Transaction Type
            processedData = await this.processTransactionTypeSummaryReport(logs);
            break;
            
          case 'apdtt': // Average Parking Duration per Transaction Type
            processedData = await this.processAverageDurationReport(logs);
            break;
            
          case 'apo': // Average Parking Occupancy
            processedData = await this.processOccupancyReport(logs, start, end);
            break;
            
          // New report types
          case 'unpaid':
            processedData = await this.processUnpaidTransactionsReport(logs);
            break;
            
          case 'voided':
            processedData = await this.processVoidedRefundedReport(logs);
            break;
            
          case 'status':
            processedData = await this.processStatusBreakdownReport(logs);
            break;
            
          case 'payment':
            processedData = await this.processRevenueByPaymentReport(logs);
            break;
            
          case 'discount':
            processedData = await this.processDiscountReport(logs);
            break;
            
          case 'duration':
            processedData = await this.processDurationDistributionReport(logs);
            break;
            
          case 'employee':
            processedData = await this.processEmployeePerformanceReport(logs);
            break;
            
          case 'trend':
            processedData = await this.processTransactionTrendReport(logs, start, end);
            break;
            
          case 'assignment':
            processedData = await this.processAssignmentTypeReport(logs);
            break;
            
          case 'vehicle': // Vehicle Type Summary
            processedData = await this.processVehicleTypeSummaryReport(logs);
            break;
            
          case 'vehicle-distribution': // Vehicle Type Distribution
            processedData = await this.processVehicleTypeDistributionReport(logs);
            break;
            
          default:
            // Fallback to processing as general report
            processedData = await this.processGeneralReport(logs);
        }
        
        // Set the processed data and update table headers
        this.tableData = processedData.data;
        this.totalTransactions = processedData.totalTransactions || 0;
        this.totalAmount = processedData.totalAmount || 0;
        
        // Update refund and cancellation information if available
        this.totalRefundedAmount = processedData.totalRefundedAmount || 0;
        this.totalCanceledCount = processedData.totalCanceledCount || 0;
        this.totalCancelledAmount = processedData.totalCancelledAmount || 0;
        this.totalUnpaidAmount = processedData.totalUnpaidAmount || 0;
        this.totalUnpaidCount = processedData.totalUnpaidCount || 0;
        this.netAmount = processedData.netAmount || this.totalAmount;
        this.totalNetAmount = processedData.totalNetAmount || 0;
        
        // Update table structure based on report type
        this.updateTableForReportType();
        
        // Show the table
        this.showResultsTable = true;
        
        // Force change detection to update the UI
        this.cdr.detectChanges();
        console.log('Table data set, showing results:', this.showResultsTable);
        
        Swal.close();
      } catch (error) {
        console.error('Error loading data:', error);
        Swal.fire({
          title: 'Error',
          text: 'Failed to load report data',
          icon: 'error'
        });
      }
    }
  }
  
  /**
   * Process logs for general report
   */
  async processGeneralReport(logs: any) {
    let arrayLogs = [];
    let totalAmount = 0;
    let totalRefundedAmount = 0;
    let totalCanceledCount = 0;
    let totalCancelledAmount = 0; // Total amount from cancelled transactions
    let totalUnpaidAmount = 0;
    let totalUnpaidCount = 0;
    let netAmount = 0;
    let totalNetAmount = 0; // This will track actual money collected (amount_paid)
    
    for (const log of logs.docs) {
      const dataLog = log.data();
      
      // Filter by employee if needed
      if (this.employeeSelected !== 'all' && 
          dataLog.additional_payment_recieved_by_id !== this.employeeSelected &&
          dataLog.initial_payment_recieved_by_id !== this.employeeSelected &&
          dataLog.paidBy_id !== this.employeeSelected) {
        continue;
      }
      
      try {
        // Process timestamps and other data
        let processedLog = this.processLogForDisplay(dataLog);
        
        // Ensure duration is calculated correctly
        if (processedLog.time_in && !processedLog.duration || processedLog.duration === 'incomplete') {
          // Calculate duration manually if needed
          if (dataLog.time_in?.seconds) {
            try {
              const startTime = moment(moment.unix(dataLog.time_in.seconds), "HH:mm:ss a");
              let endTime;
              
              if (dataLog.time_out?.seconds) {
                // If time_out exists, use it for calculation
                endTime = moment(moment.unix(dataLog.time_out.seconds), "HH:mm:ss a");
              } else {
                // If no time_out, calculate duration until current time
                endTime = moment();
                processedLog.time_out = "Active";
              }
              
              const min = moment.utc(moment(endTime, "HH:mm:ss").diff(moment(startTime, "HH:mm:ss"))).format("mm");
              const sec = moment.utc(moment(endTime, "HH:mm:ss").diff(moment(startTime, "HH:mm:ss"))).format("ss");
              const hour = endTime.diff(startTime, 'hours');
              
              if (processedLog.time_out === "Active") {
                // For active sessions, indicate it's ongoing
                if (hour === 0 && min !== "00") {
                  processedLog.duration = min + " min (Active)";
                } else if (hour === 0 && min === "00") {
                  processedLog.duration = sec + " seconds (Active)";
                } else {
                  processedLog.duration = hour + " hr " + min + " min (Active)";
                }
              } else {
                // For completed sessions
                if (hour === 0 && min !== "00") {
                  processedLog.duration = min + " min";
                } else if (hour === 0 && min === "00") {
                  processedLog.duration = sec + " seconds";
                } else {
                  processedLog.duration = hour + " hr " + min + " min";
                }
              }
            } catch (error) {
              console.error('Error calculating duration in processGeneralReport:', error);
              processedLog.duration = 'Error';
            }
          }
        }
        
        arrayLogs.push(processedLog);
        
        // Check if it's a canceled transaction
        if (processedLog.isCanceled || processedLog.status === 'cancelled') {
          totalCanceledCount++;
          // Track the cancelled amount (amount that was paid but is now cancelled)
          if (processedLog.cancel_amount) {
            totalCancelledAmount += parseFloat(processedLog.cancel_amount);
          }
        }

        // Check if it's an unpaid transaction and add to unpaid amount and count
        if (!processedLog.paid && processedLog.status !== 'cancelled' && !processedLog.isCanceled) {
          totalUnpaidCount++;
          // Add the total payment amount for unpaid transactions
          if (processedLog.total_payment) {
            totalUnpaidAmount += parseFloat(processedLog.total_payment);
          }
        }
        
        // Calculate total amount and refunded amount
        if (processedLog.total_payment) {
          totalAmount += parseFloat(processedLog.total_payment);
        }
        
        if (processedLog.refund_amount) {
          totalRefundedAmount += parseFloat(processedLog.refund_amount);
        }
        
        if (processedLog.net_payment) {
          netAmount += parseFloat(processedLog.net_payment);
        }
        
        // Calculate total net amount based on actual amount paid
        if (processedLog.net_amount) {
          totalNetAmount += parseFloat(processedLog.net_amount);
        }
      } catch (error) {
        console.error('Error processing log:', error, dataLog);
        // Continue processing other logs
      }
    }
    
    console.log('Processed logs:', arrayLogs);
    return {
      data: arrayLogs,
      totalTransactions: arrayLogs.length,
      totalAmount: totalAmount,
      totalRefundedAmount: totalRefundedAmount,
      totalCanceledCount: totalCanceledCount,
      totalCancelledAmount: totalCancelledAmount, // Total amount from cancelled transactions
      totalUnpaidAmount: totalUnpaidAmount,
      totalUnpaidCount: totalUnpaidCount,
      netAmount: netAmount, // Legacy net payment calculation
      totalNetAmount: totalNetAmount // Actual money collected based on amount_paid
    };
  }
  
  /**
   * Helper function to process a log entry for display
   */
  processLogForDisplay(dataLog: any): any {
    try {
      // Create a copy to avoid modifying original
      const data = { ...dataLog };
      
      // Process timestamps - both original and formatted versions
      if (data.time_in) {
        if (typeof data.time_in === 'string') {
          // If already formatted string (like "09/07/2025 06:41 PM")
          data.time_in_display = data.time_in;
        } else if (data.time_in.seconds) {
          // If Firestore timestamp
          try {
            const timeIn = new Date(data.time_in.seconds * 1000);
            data.time_in_display = moment(timeIn).format('MM/DD/YYYY hh:mm A');
          } catch (error) {
            console.error('Error formatting time_in:', error);
            data.time_in_display = 'Invalid date';
          }
        }
      }
      
      if (data.time_out) {
        if (typeof data.time_out === 'string') {
          // If already formatted string (like "09/07/2025 08:41 PM")
          data.time_out_display = data.time_out;
        } else if (data.time_out.seconds) {
          // If Firestore timestamp
          try {
            const timeOut = new Date(data.time_out.seconds * 1000);
            data.time_out_display = moment(timeOut).format('MM/DD/YYYY hh:mm A');
          } catch (error) {
            console.error('Error formatting time_out:', error);
            data.time_out_display = 'Invalid date';
          }
        }
      }
      
      // Process refund timestamp and data if it exists
      if (data.refunded_at) {
        try {
          const refundedAt = new Date(data.refunded_at.seconds * 1000);
          data.refunded_at_formatted = moment(refundedAt).format('MM/DD/YYYY hh:mm A');
        } catch (error) {
          console.error('Error formatting refunded_at:', error);
          data.refunded_at_formatted = 'Invalid date';
        }
      }
      
      // Store refund image URL if available
      if (data.refund_image_url) {
        data.refund_image_url = data.refund_image_url;
      }
      
      // Process cancelled timestamp if it exists
      if (data.cancelled_at) {
        try {
          const cancelledAt = new Date(data.cancelled_at.seconds * 1000);
          data.cancelled_at_formatted = moment(cancelledAt).format('MM/DD/YYYY hh:mm A');
        } catch (error) {
          console.error('Error formatting cancelled_at:', error);
          data.cancelled_at_formatted = 'Invalid date';
        }
      }
      
      // Store cancellation image URL if available
      if (data.cancellation_image_url) {
        data.cancellation_image_url = data.cancellation_image_url;
      }
      
      if (data.timestamp) {
        try {
          const timestamp = new Date(data.timestamp.seconds * 1000);
          data.fullDateTimeStamp = moment(timestamp).format("MM/DD/YYYY h:mm A");
        } catch (error) {
          console.error('Error formatting timestamp:', error);
          data.fullDateTimeStamp = 'Invalid date';
        }
      }
      
      // Process payment received dates
      if (data.initial_payment_recieved_date) {
        try {
          const initialPaymentDate = new Date(data.initial_payment_recieved_date.seconds * 1000);
          data.initial_payment_received_date_display = moment(initialPaymentDate).format('MM/DD/YYYY hh:mm A');
        } catch (error) {
          console.error('Error formatting initial_payment_recieved_date:', error);
          data.initial_payment_received_date_display = 'Invalid date';
        }
      }
      
      if (data.additional_payment_recieved_date) {
        try {
          const additionalPaymentDate = new Date(data.additional_payment_recieved_date.seconds * 1000);
          data.additional_payment_received_date_display = moment(additionalPaymentDate).format('MM/DD/YYYY hh:mm A');
        } catch (error) {
          console.error('Error formatting additional_payment_recieved_date:', error);
          data.additional_payment_received_date_display = 'Invalid date';
        }
      }
      
      // Calculate duration - enhanced logic to handle active sessions
      if (data.time_in?.seconds) {
        try {
          const startTime = moment(moment.unix(data.time_in.seconds), "HH:mm:ss a");
          let endTime;
          
          if (data.time_out?.seconds) {
            // If time_out exists, use it for calculation
            endTime = moment(moment.unix(data.time_out.seconds), "HH:mm:ss a");
          } else {
            // If no time_out, calculate duration until current time
            endTime = moment();
            data.time_out = "Active";
          }
          
          const min = moment.utc(moment(endTime, "HH:mm:ss").diff(moment(startTime, "HH:mm:ss"))).format("mm");
          const sec = moment.utc(moment(endTime, "HH:mm:ss").diff(moment(startTime, "HH:mm:ss"))).format("ss");
          const hour = endTime.diff(startTime, 'hours');
          
          if (data.time_out === "Active") {
            // For active sessions, indicate it's ongoing
            if (hour === 0 && min !== "00") {
              data.duration = min + " min (Active)";
            } else if (hour === 0 && min === "00") {
              data.duration = sec + " seconds (Active)";
            } else {
              data.duration = hour + " hr " + min + " min (Active)";
            }
          } else {
            // For completed sessions
            if (hour === 0 && min !== "00") {
              data.duration = min + " min";
            } else if (hour === 0 && min === "00") {
              data.duration = sec + " seconds";
            } else {
              data.duration = hour + " hr " + min + " min";
            }
          }
        } catch (error) {
          console.error('Error calculating duration:', error);
          data.duration = 'Error';
        }
      } else {
        // If no time_in, we can't calculate duration
        data.duration = "incomplete";
      }
      
      // Set transaction type name
      data.transaction_type_name = '';
      switch(data.transaction_type) {
        case 0:
          data.transaction_type_name = 'Regular';
          break;
        case 1:
          data.transaction_type_name = 'Valet (Guest)';
          break;
        case 2:
          data.transaction_type_name = 'Doctor (Paid)';
          break;
        case 3:
          data.transaction_type_name = 'Doctor (Free)';
          break;
        case 4:
          data.transaction_type_name = 'Valet (Guest)';
          break;
        default:
          data.transaction_type_name = 'Unknown';
      }
      
      // Process payment fields to ensure they are clean numbers
      try {
        // Parse initial_payment (ensure it's a number)
        if (data.initial_payment !== undefined && data.initial_payment !== null) {
          data.initial_payment = !isNaN(parseFloat(data.initial_payment)) ? parseFloat(data.initial_payment) : 0;
        } else {
          data.initial_payment = 0;
        }
        
        // Parse additional_payment (ensure it's a number)
        if (data.additional_payment !== undefined && data.additional_payment !== null) {
          data.additional_payment = !isNaN(parseFloat(data.additional_payment)) ? parseFloat(data.additional_payment) : 0;
        } else {
          data.additional_payment = 0;
        }
        
        // Calculate total payment
        data.total_payment = data.initial_payment + data.additional_payment;
        
        // Handle refunds
        if (data.isRefunded || data.refund_amount > 0) {
          if (data.refund_amount !== undefined && data.refund_amount !== null) {
            data.refund_amount = !isNaN(parseFloat(data.refund_amount)) ? parseFloat(data.refund_amount) : 0;
          } else {
            data.refund_amount = 0;
          }
          
          // Set refund status and info
          data.refund_status = 'Refunded';
          data.refund_info = `${data.refund_amount} - ${data.refund_reason || 'No reason provided'} - By ${data.refunded_by || 'Unknown'}`;
        } else {
          data.refund_amount = 0;
          data.refund_status = '';
          data.refund_info = '';
        }
        
        // Handle cancellations
        if (data.isCanceled || data.status === 'cancelled') {
          data.refund_status = 'Cancelled';
          data.refund_info = data.cancellation_reason ?
            `${data.cancellation_reason} - By ${data.cancelled_by || 'Unknown'}` :
            `Cancelled by ${data.cancelled_by || 'Unknown'}`;

          // For cancelled transactions, typically no refund is needed since payment wasn't made
          if (!data.refund_amount) {
            data.refund_amount = 0;
          }

          // Set cancel_amount - for cancelled transactions, this is the amount that was paid but is now cancelled
          // If the transaction was paid before being cancelled, the amount_paid represents the cancelled amount
          if (data.paid && data.amount_paid !== undefined && data.amount_paid !== null) {
            data.cancel_amount = !isNaN(parseFloat(data.amount_paid)) ? parseFloat(data.amount_paid) : 0;
          } else {
            data.cancel_amount = 0;
          }

          // Set cancel_info - formatted string with reason and who cancelled
          data.cancel_info = data.cancellation_reason ?
            `${data.cancel_amount.toFixed(2)} - ${data.cancellation_reason} - By ${data.cancelled_by || 'Unknown'}` :
            `Cancelled by ${data.cancelled_by || 'Unknown'}`;
        } else {
          // Not cancelled - set defaults
          data.cancel_amount = 0;
          data.cancel_info = '';
        }
        
        // Calculate net payment (total minus refund minus cancel) - legacy field
        data.net_payment = data.total_payment - data.refund_amount - (data.cancel_amount || 0);

        // Calculate actual net amount based on amount_paid (money actually collected)
        // Deduct refunds and cancellations from the collected amount
        if (data.amount_paid !== undefined && data.amount_paid !== null) {
          data.amount_paid = !isNaN(parseFloat(data.amount_paid)) ? parseFloat(data.amount_paid) : 0;

          // Net amount should only have value when transaction is actually paid
          // Check if transaction is paid (not unpaid/cancelled/refunded)
          if (data.paid && data.status !== 'cancelled' && !data.isCanceled && !data.isRefunded) {
            // Net amount is the actual amount paid (what was collected from the driver)
            data.net_amount = data.amount_paid;
          } else if (data.isRefunded && data.refund_amount) {
            // For refunded transactions, net_amount is amount_paid minus refund_amount
            data.net_amount = data.amount_paid - data.refund_amount;
          } else if (data.isCanceled || data.status === 'cancelled') {
            // For cancelled transactions, net_amount should be 0 (money returned/cancelled)
            data.net_amount = 0;
          } else {
            // For unpaid transactions, net_amount should be 0
            data.net_amount = 0;
          }
        } else {
          // Fallback: if no amount_paid, check payment status
          if (data.paid && data.status !== 'cancelled' && !data.isCanceled && !data.isRefunded) {
            // Use total_payment minus refunds for paid transactions
            data.net_amount = data.total_payment - data.refund_amount;
          } else if (data.isRefunded && data.refund_amount) {
            // For refunded transactions
            data.net_amount = data.total_payment - data.refund_amount;
          } else {
            // For unpaid or cancelled transactions, net_amount should be 0
            data.net_amount = 0;
          }
        }
      } catch (error) {
        console.error('Error processing payment values:', error);
      }
      
      // Ensure all required fields exist
      data.zone_name = data.zone_name || data.station_name || '';
      data.slot_name = data.slot_name || '';
      data.plate_number = data.plate_number || '';
      data.vehicle_type_custom_name = data.vehicle_type_custom_name || '';
      data.processedBy = data.processedBy || '';
      
      // Handle "Ended By" logic - check if ended by user or server
      if (data.session_ended_by) {
        // Session ended by a user
        data.end_session_by = data.session_ended_by;
      } else if (data.being_processed_by) {
        // Session ended by server/system
        if (data.being_processed_by === 'server_background_completed') {
          data.end_session_by = 'System (Auto-completed)';
        } else if (data.being_processed_by.startsWith('server_')) {
          data.end_session_by = 'System';
        } else {
          data.end_session_by = data.being_processed_by;
        }
      } else {
        // Fallback to original field or empty
        const isManualAssignment = this.selectedZone?.zone_slot_assignment === ZoneSlotAssignment.Manual; // ZoneSlotAssignment.Manual
        data.end_session_by = data.end_session_by || '';

        // If manual assignment, empty end_session_by, and status is completed, assume System ended it
        if (isManualAssignment && !data.end_session_by && data.status === 'completed') {
          data.end_session_by = 'System';
        }
      }
      
      // Process payment received by information
      data.initial_payment_recieved_by = data.initial_payment_recieved_by || '';
      data.additional_payment_recieved_by = data.additional_payment_recieved_by || '';
      
      // Process discount information
      data.discount_details = data.discount_details || '';
      data.discounted_amount = data.discounted_amount || 0;
      data.discount_savings = data.discount_savings || 0;
      
      // If no discounted_amount but there's discount info, use discount_savings or calculate it
      if (!data.discounted_amount && data.discount_savings) {
        data.discounted_amount = parseFloat(data.discount_savings) || 0;
      } else if (!data.discounted_amount && data.discount_percent && data.original_amount) {
        data.discounted_amount = (parseFloat(data.original_amount) * parseFloat(data.discount_percent) / 100) || 0;
      }
      
      // Ensure discount_savings is properly parsed
      if (data.discount_savings !== undefined && data.discount_savings !== null) {
        data.discount_savings = !isNaN(parseFloat(data.discount_savings)) ? parseFloat(data.discount_savings) : 0;
      }
      
      // Set display date (use fullDateTimeStamp or time_in)
      data.date_display = data.fullDateTimeStamp || data.time_in_display || '';
      
      // Add a status field for easier filtering and display
      if (data.isCanceled || data.status === 'cancelled') {
        data.status_text = 'Cancelled';
      } else if (data.isRefunded) {
        data.status_text = 'Refunded';
      } else if (data.paid) {
        data.status_text = 'Completed';
      } else {
        data.status_text = 'Unpaid';
      }
      
      return data;
    } catch (error) {
      console.error('Error in processLogForDisplay:', error);
      return dataLog; // Return original if processing fails
    }
  }

  /**
   * Process logs for summary per attendant report
   */
  async processAttendantSummaryReport(logs: any) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    const attendantData = {};
    let totalAmount = 0;
    
    // Process logs to create attendant summary
    for (const log of logs.docs) {
      const dataLog = log.data();
      
      // Get the date and attendant information
      const date = moment(dataLog.time_in.seconds * 1000).format('MMM DD YYYY');
      let processedBy = dataLog.processedBy || 'Unknown';
      
      // Filter by employee if needed
      if (this.employeeSelected !== 'all' && 
          dataLog.additional_payment_recieved_by_id !== this.employeeSelected &&
          dataLog.initial_payment_recieved_by_id !== this.employeeSelected &&
          dataLog.paidBy_id !== this.employeeSelected) {
        continue;
      }
      
      // Initialize date entry if it doesn't exist
      if (!attendantData[date]) {
        attendantData[date] = {
          date: date,
          headers: {
            values: []
          }
        };
      }
      
      // Calculate total payment from all possible payment fields
      let amount = 0;
      
      // Check if the transaction is paid
      const isPaid = dataLog.paid === true;
      
      // For paid transactions, use the actual payment amount
      if (isPaid) {
        // Check for amount_paid first (most direct payment field)
        if (dataLog.amount_paid !== undefined && dataLog.amount_paid !== null && !isNaN(parseFloat(dataLog.amount_paid))) {
          amount = parseFloat(dataLog.amount_paid);
        } 
        // If no amount_paid, try initial_payment + additional_payment
        else if ((dataLog.initial_payment !== undefined && dataLog.initial_payment !== null) || 
                 (dataLog.additional_payment !== undefined && dataLog.additional_payment !== null)) {
          const initialPayment = parseFloat(dataLog.initial_payment || 0);
          const additionalPayment = parseFloat(dataLog.additional_payment || 0);
          amount = initialPayment + additionalPayment;
        } 
        // If neither available, try total_payment
        else if (dataLog.total_payment !== undefined && dataLog.total_payment !== null && !isNaN(parseFloat(dataLog.total_payment))) {
          amount = parseFloat(dataLog.total_payment);
        }
        // If still no amount, use amount_due (might be the case for some transactions)
        else if (dataLog.amount_due !== undefined && dataLog.amount_due !== null && !isNaN(parseFloat(dataLog.amount_due))) {
          amount = parseFloat(dataLog.amount_due);
        }
        
        // Add discount savings if available (this might be missing in some reports)
        if (dataLog.discount_savings !== undefined && dataLog.discount_savings !== null && !isNaN(parseFloat(dataLog.discount_savings))) {
          amount += parseFloat(dataLog.discount_savings);
        }
      } 
      // For unpaid transactions with amount_due, use that as the expected revenue
      else if (dataLog.amount_due !== undefined && dataLog.amount_due !== null && !isNaN(parseFloat(dataLog.amount_due))) {
        amount = parseFloat(dataLog.amount_due);
      }
      
      // Skip entries with zero payment and no payment fields
      if (amount === 0 && !isPaid && dataLog.payment_type === 0) {
        continue;
      }
      
      totalAmount += amount;
      
      // Find or create attendant entry
      let attendantEntry = attendantData[date].headers.values.find(v => v.name === processedBy);
      
      if (!attendantEntry) {
        attendantEntry = {
          name: processedBy,
          count: 0,
          sales: 0
        };
        attendantData[date].headers.values.push(attendantEntry);
      }
      
      // Update attendant stats
      attendantEntry.count++;
      attendantEntry.sales += amount;
    }
    
    // Convert to array
    const result = Object.values(attendantData);
    
    console.log('Processed attendant summary:', result);
    return {
      data: result,
      totalTransactions: summaryMetrics.totalTransactions,
      totalAmount: totalAmount, // Use the locally calculated amount from attendant summary logic
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }
  
  /**
   * Process logs for summary per transaction type report
   */
  async processTransactionTypeSummaryReport(logs: any) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    const typeData = {};
    let totalAmount = 0;
    
    // Process logs to create transaction type summary
    for (const log of logs.docs) {
      const dataLog = log.data();
      console.log('dataLog', dataLog);
      
      // Get the date and transaction type
      const date = moment(dataLog.time_in.seconds * 1000).format('MMM DD YYYY');
      let transactionType = dataLog.transaction_type_name || 'Unknown';
      
      // If transaction_type_name is not available, determine from transaction_type
      if (!dataLog.transaction_type_name) {
        switch (dataLog.transaction_type) {
          case 0:
            transactionType = 'Regular';
            break;
          case 1:
            transactionType = 'Valet';
            break;
          case 2:
            transactionType = 'VIP';
            break;
          case 3:
            transactionType = 'Reserved';
            break;
          default:
            transactionType = 'Unknown';
        }
      }
      
      // Filter by employee if needed
      if (this.employeeSelected !== 'all' && 
          dataLog.additional_payment_recieved_by_id !== this.employeeSelected &&
          dataLog.initial_payment_recieved_by_id !== this.employeeSelected &&
          dataLog.paidBy_id !== this.employeeSelected) {
        continue;
      }
      
      // Initialize date entry if it doesn't exist
      if (!typeData[date]) {
        typeData[date] = {
          date: date,
          headers: {
            values: []
          }
        };
      }
      
      // Calculate total payment from all possible payment fields
      // First try amount_paid, then initial_payment + additional_payment, then total_payment
      let amount = 0;
      if (dataLog.amount_paid && !isNaN(parseFloat(dataLog.amount_paid))) {
        amount = parseFloat(dataLog.amount_paid);
      } else if (dataLog.initial_payment || dataLog.additional_payment) {
        const initialPayment = parseFloat(dataLog.initial_payment || 0);
        const additionalPayment = parseFloat(dataLog.additional_payment || 0);
        amount = initialPayment + additionalPayment;
      } else if (dataLog.total_payment && !isNaN(parseFloat(dataLog.total_payment))) {
        amount = parseFloat(dataLog.total_payment);
      }
      
      totalAmount += amount;
      
      // Find or create transaction type entry
      let typeEntry = typeData[date].headers.values.find(v => v.name === transactionType);
      
      if (!typeEntry) {
        typeEntry = {
          name: transactionType,
          count: 0,
          sales: 0
        };
        typeData[date].headers.values.push(typeEntry);
      }
      
      // Update transaction type stats
      typeEntry.count++;
      typeEntry.sales += amount;
    }
    
    // Convert to array
    const result = Object.values(typeData);
    
    console.log('Processed transaction type summary:', result);
    return {
      data: result,
      totalTransactions: summaryMetrics.totalTransactions,
      totalAmount: totalAmount, // Use the locally calculated amount from transaction type summary logic
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }
  
  /**
   * Process logs for average parking duration report
   */
  async processAverageDurationReport(logs: any) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    const durationData = {};
    let totalAmount = 0;
    
    // Process logs to create average duration data
    for (const log of logs.docs) {
      const dataLog = log.data();
      
      // Skip logs without time_out
      if (!dataLog.time_out) continue;
      
      // Get the transaction type
      let transactionType = dataLog.transaction_type_name || 'Unknown';
      
      // Filter by employee if needed
      if (this.employeeSelected !== 'all' && 
          dataLog.additional_payment_recieved_by_id !== this.employeeSelected &&
          dataLog.initial_payment_recieved_by_id !== this.employeeSelected &&
          dataLog.paidBy_id !== this.employeeSelected) {
        continue;
      }
      
      // Calculate duration in minutes
      const timeIn = dataLog.time_in.seconds * 1000;
      const timeOut = dataLog.time_out.seconds * 1000;
      const durationMinutes = (timeOut - timeIn) / (1000 * 60);
      
      // Get amount
      const amount = parseFloat(dataLog.total_payment || 0);
      totalAmount += amount;
      
      // Initialize transaction type entry if it doesn't exist
      if (!durationData[transactionType]) {
        durationData[transactionType] = {
          transactionTypeName: transactionType,
          totalDurationMinutes: 0,
          vehicleCount: 0
        };
      }
      
      // Update transaction type stats
      durationData[transactionType].totalDurationMinutes += durationMinutes;
      durationData[transactionType].vehicleCount++;
    }
    
    // Calculate averages
    Object.values(durationData).forEach((entry: any) => {
      entry.averageDurationMinutes = entry.totalDurationMinutes / entry.vehicleCount;
      entry.averageDurationHours = entry.averageDurationMinutes / 60;
    });
    
    // Convert to array
    const result = Object.values(durationData);
    
    console.log('Processed average duration data:', result);
    return {
      data: result,
      totalTransactions: summaryMetrics.totalTransactions,
      totalAmount: totalAmount, // Use the locally calculated amount from average duration logic
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }
  
  /**
   * Process logs for occupancy report
   */
  async processOccupancyReport(logs: any, startDate: Date, endDate: Date) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);
    // Define time ranges for occupancy tracking (every hour)
    const timeRanges = [];
    for (let hour = 0; hour < 24; hour++) {
      const startHour = hour.toString().padStart(2, '0');
      const endHour = (hour + 1).toString().padStart(2, '0');
      timeRanges.push({
        timeRange: `${startHour}:00 - ${endHour}:00`,
        startHour: hour,
        dates: []
      });
    }
    
    // Create dates array for the date range
    const dates = [];
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(moment(currentDate).format('YYYY-MM-DD'));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Get the total capacity (parking slots) for the selected zone
    const zoneCapacity = this.selectedZone?.capacity || 50; // Default capacity if not specified
    
    // Process logs to create occupancy data
    for (const date of dates) {
      const dateMoment = moment(date);
      const dateFormatted = dateMoment.format('MMM DD YYYY');
      
      // For each time range
      for (const timeRange of timeRanges) {
        // Count vehicles present during this time range for this date
        let vehiclesPresent = 0;
        
        // Check each log entry
        for (const log of logs.docs) {
          const dataLog = log.data();
          
          // Get time in/out as moments
          const timeIn = moment(dataLog.time_in.seconds * 1000);
          const timeOut = dataLog.time_out ? moment(dataLog.time_out.seconds * 1000) : moment();
          
          // Check if log date matches current date
          if (timeIn.format('YYYY-MM-DD') !== date && timeOut.format('YYYY-MM-DD') !== date) {
            continue;
          }
          
          // Check if vehicle was present in this time range
          const rangeStart = moment(date).hour(timeRange.startHour).minute(0).second(0);
          const rangeEnd = moment(date).hour(timeRange.startHour + 1).minute(0).second(0);
          
          if ((timeIn.isBefore(rangeEnd) && timeOut.isAfter(rangeStart))) {
            vehiclesPresent++;
          }
        }
        
        // Calculate occupancy rate
        const occupancyRate = (vehiclesPresent / zoneCapacity) * 100;
        
        // Add to time range data
        timeRange.dates.push({
          date: dateFormatted,
          vehiclesPresent: vehiclesPresent,
          occupancyRate: Math.min(occupancyRate, 100) // Cap at 100%
        });
      }
    }
    
    console.log('Processed occupancy data:', timeRanges);
    return {
      data: timeRanges,
      totalTransactions: summaryMetrics.totalTransactions,
      totalAmount: summaryMetrics.totalAmount,
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }

  // Helper methods moved to avoid duplication

  /**
   * Export to CSV directly from the current displayed data
   */
  exportToCsv() {
    // Make sure we have data to export
    if (!this.tableData || this.tableData.length === 0) {
      Swal.fire({
        title: 'No Data',
        text: 'There is no data to export. Please generate a report first.',
        icon: 'warning'
      });
      return;
    }

    try {
      // Show loading indicator
      Swal.fire({
        title: 'Generating CSV...',
        text: 'Please wait while we prepare your file.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Prepare data based on report type
      let csvData = [];
      const fileName = `${this.selectedReportType}_report_${new Date().toISOString().slice(0,10)}.csv`;
      
      // Add header row with company info
      csvData.push(['Company', this.selectedZone?.zone_name || 'All Zones']);
      csvData.push(['Report Type', this.getReportTypeDisplayName()]);
      csvData.push(['Period', this.reportPeriod]);
      csvData.push(['Generated On', new Date().toLocaleString()]);
      csvData.push(['']); // Empty row as separator
      
      switch (this.selectedReportType) {
        case 'general':
          // Add headers
          csvData.push([
            'Date', 'Plate Number', 'Zone Name', 'Slot', 'Vehicle Type', 'Transaction Type',
            'Start', 'Started By', 'End', 'Ended By', 'Status', 'Duration',
            'Initial Rate', 'Initial Rate Received By', 'Initial Rate Received Date',
            'Additional Payment', 'Additional Payment Received By', 'Additional Payment Received Date',
            'Refund Amount', 'Refund Info', 'Cancel Amount', 'Cancel Info',
            'Total Payment', 'Discount Savings', 'Discounted Amount', 'Discount Details', 'Net Amount'
          ]);

          // Add data rows
          this.getFilteredData().forEach(row => {
            // Calculate status
            let status = '-';
            if (row.entry_status && row.exit_status) {
              status = 'Completed';
            } else if (row.entry_status && !row.exit_status) {
              status = 'Active';
            }
            
            csvData.push([
              row.date_display || '',
              row.plate_number || '',
              row.zone_name || '',
              row.slot_name || '',
              row.vehicle_type_custom_name || '',
              row.transaction_type_name || '',
              row.time_in_display || '',
              row.processedBy || '',
              row.time_out_display || '',
              row.end_session_by || '',
              status,
              row.duration || '',
              row.initial_payment ? row.initial_payment.toFixed(2) : '0.00',
              row.initial_payment_recieved_by || '',
              row.initial_payment_received_date_display || '',
              row.additional_payment ? row.additional_payment.toFixed(2) : '0.00',
              row.additional_payment_recieved_by || '',
              row.additional_payment_received_date_display || '',
              row.refund_amount ? row.refund_amount.toFixed(2) : '0.00',
              row.refund_info || '',
              row.cancel_amount ? row.cancel_amount.toFixed(2) : '0.00',
              row.cancel_info || '',
              row.total_payment ? row.total_payment.toFixed(2) : '0.00',
              row.discount_savings ? row.discount_savings.toFixed(2) : '0.00',
              row.discounted_amount ? row.discounted_amount.toFixed(2) : '0.00',
              row.discount_details || '',
              row.net_amount ? row.net_amount.toFixed(2) : '0.00'
            ]);
          });

          // Add summary row
          csvData.push(['']);
          csvData.push(['Total Transactions', this.totalTransactions]);
          csvData.push(['Total Amount', this.totalAmount.toFixed(2)]);
          if (this.totalCanceledCount > 0) {
            csvData.push(['Cancelled Transactions', `${this.totalCanceledCount} (₱${this.totalCancelledAmount.toFixed(2)})`]);
          }
          if (this.totalUnpaidCount > 0) {
            csvData.push(['Unpaid Transactions', `${this.totalUnpaidAmount.toFixed(2)} (${this.totalUnpaidCount})`]);
          }
          csvData.push(['Total Refunded Amount', this.totalRefundedAmount ? this.totalRefundedAmount.toFixed(2) : '0.00']);
          csvData.push(['Net Amount (Collected)', this.totalNetAmount ? this.totalNetAmount.toFixed(2) : '0.00']);
          break;
          
        case 'dsa': // Summary per Attendant
          // Transaction count section
          csvData.push(['Total Transactions', this.totalTransactions]);
          csvData.push(['']); // Empty row as separator

          // Add headers for transaction count
          const countHeaders = ['Date', ...this.summaryHeaders, 'Total'];
          csvData.push(countHeaders);

          // Add transaction count data rows
          this.getFilteredData().forEach(row => {
            const dataRow = [row.date];

            this.summaryHeaders.forEach(header => {
              dataRow.push(this.getSummaryValue(row, header, 'count'));
            });

            dataRow.push(this.getTotalForRow(row, 'count'));
            csvData.push(dataRow);
          });

           // Add totals row for count
           const countTotalsRow = ['GRAND TOTAL'];
           this.summaryHeaders.forEach(header => {
             countTotalsRow.push(String(this.getColumnTotal(header, 'count')));
           });
           countTotalsRow.push(String(this.totalTransactions));
           csvData.push(countTotalsRow);

          // Empty row as separator
          csvData.push(['']);

          // Sales section
          csvData.push(['Total Sales', this.totalAmount.toFixed(2)]);

          // Add refund information if available
          if (this.totalRefundedAmount > 0) {
            csvData.push(['Total Refunded Amount', this.totalRefundedAmount.toFixed(2)]);
            csvData.push(['Net Amount', this.netAmount.toFixed(2)]);
          }
          if (this.totalUnpaidCount > 0) {
            csvData.push(['Unpaid Transactions', `${this.totalUnpaidAmount.toFixed(2)} (${this.totalUnpaidCount})`]);
          }

          csvData.push(['']); // Empty row as separator
          
          // Add headers for sales
          csvData.push(countHeaders);
          
          // Add sales data rows
          this.getFilteredData().forEach(row => {
            const dataRow = [row.date];
            
            this.summaryHeaders.forEach(header => {
              dataRow.push(this.getSummaryValue(row, header, 'sales').toFixed(2));
            });
            
            dataRow.push(this.getTotalForRow(row, 'sales').toFixed(2));
            csvData.push(dataRow);
          });
          
          // Add totals row for sales
          const salesTotalsRow = ['GRAND TOTAL'];
          this.summaryHeaders.forEach(header => {
            salesTotalsRow.push(this.getColumnTotal(header, 'sales').toFixed(2));
          });
          salesTotalsRow.push(this.totalAmount.toFixed(2));
          csvData.push(salesTotalsRow);
          break;
          
        case 'vehicle': // Summary per Vehicle Type
          // Similar structure to DSA report
          csvData.push(['Total Transactions', this.totalTransactions]);
          csvData.push(['']); // Empty row as separator

          // Add headers for transaction count
          const vehicleHeaders = ['Date', ...this.summaryHeaders, 'Total'];
          csvData.push(vehicleHeaders);

          // Add transaction count data rows
          this.getFilteredData().forEach(row => {
            const dataRow = [row.date];

            this.summaryHeaders.forEach(header => {
              dataRow.push(this.getSummaryValue(row, header, 'count'));
            });

            dataRow.push(this.getTotalForRow(row, 'count'));
            csvData.push(dataRow);
          });

           // Add totals row for count
           const vehicleCountTotalsRow = ['GRAND TOTAL'];
           this.summaryHeaders.forEach(header => {
             vehicleCountTotalsRow.push(String(this.getColumnTotal(header, 'count')));
           });
           vehicleCountTotalsRow.push(String(this.totalTransactions));
           csvData.push(vehicleCountTotalsRow);

          // Empty row as separator
          csvData.push(['']);

          // Sales section
          csvData.push(['Total Sales', this.totalAmount.toFixed(2)]);

          // Add refund information if available
          if (this.totalRefundedAmount > 0) {
            csvData.push(['Total Refunded Amount', this.totalRefundedAmount.toFixed(2)]);
            csvData.push(['Net Amount', this.netAmount.toFixed(2)]);
          }
          if (this.totalUnpaidCount > 0) {
            csvData.push(['Unpaid Transactions', `${this.totalUnpaidAmount.toFixed(2)} (${this.totalUnpaidCount})`]);
          }

          csvData.push(['']); // Empty row as separator
          
          // Add headers for sales
          csvData.push(vehicleHeaders);
          
          // Add sales data rows
          this.getFilteredData().forEach(row => {
            const dataRow = [row.date];
            
            this.summaryHeaders.forEach(header => {
              dataRow.push(this.getSummaryValue(row, header, 'sales').toFixed(2));
            });
            
            dataRow.push(this.getTotalForRow(row, 'sales').toFixed(2));
            csvData.push(dataRow);
          });
          
          // Add totals row for sales
          const vehicleSalesTotalsRow = ['GRAND TOTAL'];
          this.summaryHeaders.forEach(header => {
            vehicleSalesTotalsRow.push(this.getColumnTotal(header, 'sales').toFixed(2));
          });
          vehicleSalesTotalsRow.push(this.totalAmount.toFixed(2));
          csvData.push(vehicleSalesTotalsRow);
          break;
          
        case 'vehicle-distribution':
          // Add headers
          csvData.push(['Vehicle Type', 'Count', 'Percentage', 'Revenue', 'Avg Revenue/Vehicle']);
          
          // Add data rows
          this.getFilteredData().forEach(row => {
            csvData.push([
              row.vehicle_type || '',
              row.count || 0,
              row.percentage || '0%',
              row.revenue ? row.revenue.toFixed(2) : '0.00',
              row.avg_revenue_per_vehicle || '0.00'
            ]);
          });
          
          // Add summary row
          csvData.push(['']);
          csvData.push(['Total Transactions', this.totalTransactions]);
          csvData.push(['Total Amount', this.totalAmount.toFixed(2)]);

          // Add refund information if available
          if (this.totalRefundedAmount > 0) {
            csvData.push(['Total Refunded Amount', this.totalRefundedAmount.toFixed(2)]);
            csvData.push(['Net Amount', this.netAmount.toFixed(2)]);
          }
          if (this.totalUnpaidCount > 0) {
            csvData.push(['Unpaid Transactions', `${this.totalUnpaidAmount.toFixed(2)} (${this.totalUnpaidCount})`]);
          }
          break;
          
        default:
          // For other report types, use table headers and data directly
          const headers = this.tableHeaders.length > 0 ? this.tableHeaders : Object.keys(this.tableData[0] || {});
          csvData.push(headers);
          
          this.getFilteredData().forEach(row => {
            const rowData = [];
            headers.forEach(header => {
              const propName = this.getPropertyFromHeader(header);
              let value = row[propName];
              
              // Format currency values without currency symbol
              if (this.isCurrencyField(propName) && value !== undefined && value !== null) {
                value = parseFloat(value).toFixed(2);
              }
              
              rowData.push(value !== undefined && value !== null ? value : '');
            });
            csvData.push(rowData);
          });
          
          // Add summary row
          csvData.push(['']);
          csvData.push(['Total Transactions', this.totalTransactions]);
          csvData.push(['Total Amount', this.totalAmount.toFixed(2)]);

          // Add refund information if available
          if (this.totalRefundedAmount > 0) {
            csvData.push(['Total Refunded Amount', this.totalRefundedAmount.toFixed(2)]);
            csvData.push(['Net Amount', this.netAmount.toFixed(2)]);
          }
          if (this.totalUnpaidCount > 0) {
            csvData.push(['Unpaid Transactions', `${this.totalUnpaidAmount.toFixed(2)} (${this.totalUnpaidCount})`]);
          }
      }
      
      // Convert to CSV string
      let csvContent = '';
      csvData.forEach(row => {
        // Properly escape CSV values
        const escapedRow = row.map(value => {
          if (value === null || value === undefined) return '';
          const str = String(value);
          // Escape quotes and wrap in quotes if needed
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return '"' + str.replace(/"/g, '""') + '"';
          }
          return str;
        });
        csvContent += escapedRow.join(',') + '\n';
      });
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      Swal.fire({
        title: 'Success',
        text: 'CSV file has been downloaded successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to export to CSV. Please try again.',
        icon: 'error'
      });
    }
  }
  
  /**
   * Get user-friendly report type name
   */
  getReportTypeDisplayName(): string {
    const reportTypeMap = {
      'general': 'General Report',
      'dsa': 'Summary per Attendant',
      'vehicle': 'Summary per Vehicle Type',
      'vehicle-distribution': 'Vehicle Type Distribution',
      'employee': 'Employee Performance Report',
      'status': 'Transaction Status Breakdown',
      'payment': 'Revenue by Payment Type',
      'duration': 'Parking Duration Distribution',
      'unpaid': 'Unpaid Transactions Summary',
      'voided': 'Voided/Refunded Transactions'
    };
    
    return reportTypeMap[this.selectedReportType] || this.selectedReportType;
  }

  // This method has been replaced by the PdfImageService

  // This method has been replaced by the PdfImageService

  /**
   * Export to PDF
   */
  exportToPdf() {
    // Make sure we have data to export
    if (!this.tableData || this.tableData.length === 0) {
      Swal.fire({
        title: 'No Data',
        text: 'There is no data to export. Please generate a report first.',
        icon: 'warning'
      });
      return;
    }

    try {
      // Show loading indicator
      Swal.fire({
        title: 'Generating PDF...',
        text: 'Please wait while we prepare your file.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      import('jspdf').then((jsPDF) => {
        import('jspdf-autotable').then(() => {
          // Create new PDF document
          const doc = new jsPDF.default('landscape', 'mm', 'a4');
          
          // Use the PDF image service to load and add the logo
          const imageUrl = this.account?.photoUrl || 'assets/img/logo_new.png';
          this.pdfImageService.loadImageForPDF(imageUrl, doc, (contentStartY, logoWidth) => {
            // Add header
            doc.setFontSize(18);
            doc.text(`${this.getReportTypeDisplayName() || 'General Report'}`, 14, contentStartY);
            
            // Add metadata - First column
            doc.setFontSize(10);
            doc.text(`Company: ${this.account.account_name || '-'}`, 14, contentStartY + 10);
            doc.text(`Zone: ${this.selectedZone?.zone_name || 'All Zones'}`, 14, contentStartY + 15);
            doc.text(`Cashier: ${this.employeeSelected || 'All Cashiers'}`, 14, contentStartY + 20);
            doc.text(`Report Type: ${this.getReportTypeDisplayName() || 'General Report'}`, 14, contentStartY + 25);
            doc.text(`Period: ${this.reportPeriod}`, 14, contentStartY + 30);
            doc.text(`Generated On: ${new Date().toLocaleString()}`, 14, contentStartY + 35);
            
            // Add summary data - Second column
            const secondColumnX = 150;
            let secondColumnY = contentStartY + 10;
            
            doc.text(`Total Transactions: ${this.totalTransactions}`, secondColumnX, secondColumnY);
            secondColumnY += 5;
            doc.text(`Total Amount: ${this.totalAmount.toFixed(2)}`, secondColumnX, secondColumnY);
             
            // Add cancelled transactions count and amount if available
            if (this.totalCanceledCount > 0) {
              secondColumnY += 5;
              doc.text(`Cancelled Transactions: ${this.totalCanceledCount} (₱${this.totalCancelledAmount.toFixed(2)})`, secondColumnX, secondColumnY);
            }

            // Add unpaid transactions amount and count if available
            if (this.totalUnpaidCount > 0) {
              secondColumnY += 5;
              doc.text(`Unpaid Transactions: ${this.totalUnpaidAmount.toFixed(2)} (${this.totalUnpaidCount})`, secondColumnX, secondColumnY);
            }

            // Add refund information if available
            if (this.totalRefundedAmount > 0) {
              secondColumnY += 5;
              doc.text(`Total Refunded Amount: ${this.totalRefundedAmount.toFixed(2)}`, secondColumnX, secondColumnY);
            }

            // Always show Net Amount (Collected)
            secondColumnY += 5;
            doc.text(`Net Amount (Collected): ${this.totalNetAmount.toFixed(2)}`, secondColumnX, secondColumnY);
            
            // Prepare table data
            const tableColumn = this.tableHeaders;
            const tableRows = [];
            
            // Add data rows
            this.getFilteredData().forEach(row => {
              let rowData;
              if (this.selectedReportType === 'general') {
                rowData = [
                  row.date_display || '',
                  row.plate_number || '',
                  row.zone_name || '',
                  row.slot_name || '',
                  row.vehicle_type_custom_name || '',
                  row.transaction_type_name || '',
                  row.time_in_display || '',
                  row.processedBy || '',
                  row.time_out_display || '',
                  row.end_session_by || '',
                  row.duration || '',
                  row.initial_payment ? row.initial_payment.toFixed(2) : '0.00',
                  row.initial_payment_recieved_by || '',
                  row.initial_payment_received_date_display || '',
                  row.additional_payment ? row.additional_payment.toFixed(2) : '0.00',
                  row.additional_payment_recieved_by || '',
                  row.additional_payment_received_date_display || '',
                  row.refund_amount ? row.refund_amount.toFixed(2) : '0.00',
                  row.refund_info || '',
                  row.cancel_amount ? row.cancel_amount.toFixed(2) : '0.00',
                  row.cancel_info || '',
                  row.total_payment ? row.total_payment.toFixed(2) : '0.00',
                  row.discount_savings ? row.discount_savings.toFixed(2) : '0.00',
                  row.discounted_amount ? row.discounted_amount.toFixed(2) : '0.00',
                  row.discount_details || '',
                  row.net_amount ? row.net_amount.toFixed(2) : '0.00'
                ];
              } else {
                // For other report types, handle based on the columns
                rowData = tableColumn.map(header => {
                  const prop = this.getPropertyFromHeader(header);
                  const val = row[prop];
                  
                  if (typeof val === 'number') {
                    return val.toFixed(2);
                  }
                  return val || '';
                });
              }
              tableRows.push(rowData);
            });
            
            // Create the table - adjust starting Y position
            let startY = contentStartY + 45; // Base position after all header text
            
            (doc as any).autoTable({
              head: [tableColumn],
              body: tableRows,
              startY: startY,
              styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: 'linebreak'
              },
              headStyles: {
                fillColor: [66, 66, 66],
                textColor: 255,
                fontStyle: 'bold'
              },
              alternateRowStyles: {
                fillColor: [240, 240, 240]
              },
              margin: { top: 60 }
            });
            
            // Save the PDF
            const reportTypeName = this.selectedReportType || 'general';
            const fileName = `${reportTypeName}_report_${new Date().toISOString().slice(0,10)}.pdf`;
            doc.save(fileName);
            
            // Show success message
            Swal.fire({
              title: 'Success',
              text: 'PDF file has been downloaded successfully.',
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
          });
        });
      });
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to export to PDF. Please try again.',
        icon: 'error'
      });
    }
  }

  /**
   * Print report using browser's native print function (optimized and fast)
   */
  printReport(): void {
    if (!this.tableData || this.tableData.length === 0) {
      Swal.fire({
        title: 'No Data',
        text: 'There is no data to print. Please generate a report first.',
        icon: 'warning'
      });
      return;
    }

    // Build table HTML based on report type
    let tableHtml = '';
    const data = this.getFilteredData();

    if (this.selectedReportType === 'general') {
      tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Plate #</th>
              <th>Zone</th>
              <th>Vehicle Type</th>
              <th>Start</th>
              <th>End</th>
              <th>Duration</th>
              <th>Initial</th>
              <th>Additional</th>
              <th>Total</th>
              <th>Net Amount</th>
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                <td>${row.date_display || ''}</td>
                <td>${row.plate_number || ''}</td>
                <td>${row.zone_name || ''}</td>
                <td>${row.vehicle_type_custom_name || ''}</td>
                <td>${row.time_in_display || ''}</td>
                <td>${row.time_out_display || ''}</td>
                <td>${row.duration || ''}</td>
                <td class="amount">₱${row.initial_payment ? row.initial_payment.toFixed(2) : '0.00'}</td>
                <td class="amount">₱${row.additional_payment ? row.additional_payment.toFixed(2) : '0.00'}</td>
                <td class="amount">₱${row.total_payment ? row.total_payment.toFixed(2) : '0.00'}</td>
                <td class="amount net">₱${row.net_amount ? row.net_amount.toFixed(2) : '0.00'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>`;
    } else if (this.selectedReportType === 'dsa' || this.selectedReportType === 'vehicle') {
      // Summary per Attendant or Vehicle Type
      tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Date</th>
              ${this.summaryHeaders.map(h => `<th>${h}</th>`).join('')}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                <td class="date">${row.date}</td>
                ${this.summaryHeaders.map(h => `
                  <td>
                    <div>Count: ${this.getSummaryValue(row, h, 'count')}</div>
                    <div>₱${this.getSummaryValue(row, h, 'sales').toFixed(2)}</div>
                  </td>
                `).join('')}
                <td class="total">
                  <div>Count: ${this.getTotalForRow(row, 'count')}</div>
                  <div>₱${this.getTotalForRow(row, 'sales').toFixed(2)}</div>
                </td>
              </tr>
            `).join('')}
            <tr class="grand-total">
              <td><strong>GRAND TOTAL</strong></td>
              ${this.summaryHeaders.map(h => `
                <td>
                  <div><strong>${this.getColumnTotal(h, 'count')}</strong></div>
                  <div><strong>₱${this.getColumnTotal(h, 'sales').toFixed(2)}</strong></div>
                </td>
              `).join('')}
              <td>
                <div><strong>${this.totalTransactions}</strong></div>
                <div><strong>₱${this.totalAmount.toFixed(2)}</strong></div>
              </td>
            </tr>
          </tbody>
        </table>`;
    } else if (this.selectedReportType === 'employee') {
      tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Transactions Started</th>
              <th>Transactions Completed</th>
              <th>Total Revenue</th>
              <th>Avg Transaction Value</th>
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                <td>${row.employee_name || ''}</td>
                <td>${row.transactions_started || 0}</td>
                <td>${row.transactions_completed || 0}</td>
                <td class="amount">₱${row.total_revenue ? row.total_revenue.toFixed(2) : '0.00'}</td>
                <td class="amount">₱${row.average_transaction_value || '0.00'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>`;
    } else if (this.selectedReportType === 'status') {
      tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Count</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                <td>${row.status || ''}</td>
                <td>${row.count || 0}</td>
                <td>${row.percentage || '0%'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>`;
    } else if (this.selectedReportType === 'payment') {
      tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Payment Type</th>
              <th>Transaction Count</th>
              <th>Total Amount</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                <td>${row.payment_type || ''}</td>
                <td>${row.transaction_count || 0}</td>
                <td class="amount">₱${row.total_amount ? row.total_amount.toFixed(2) : '0.00'}</td>
                <td>${row.percentage_of_revenue || '0%'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>`;
    } else if (this.selectedReportType === 'duration') {
      tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Duration Range</th>
              <th>Count</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                <td>${row.duration_range || ''}</td>
                <td>${row.count || 0}</td>
                <td>${row.percentage || '0%'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>`;
    } else if (this.selectedReportType === 'vehicle-distribution') {
      tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Vehicle Type</th>
              <th>Count</th>
              <th>Percentage</th>
              <th>Revenue</th>
              <th>Avg Revenue/Vehicle</th>
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                <td>${row.vehicle_type || ''}</td>
                <td>${row.count || 0}</td>
                <td>${row.percentage || '0%'}</td>
                <td class="amount">₱${row.revenue ? row.revenue.toFixed(2) : '0.00'}</td>
                <td class="amount">₱${row.avg_revenue_per_vehicle || '0.00'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>`;
    } else {
      // Generic table for other report types
      const headers = this.tableHeaders.length > 0 ? this.tableHeaders : Object.keys(data[0] || {});
      tableHtml = `
        <table>
          <thead>
            <tr>
              ${headers.map(h => `<th>${h}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${data.map(row => `
              <tr>
                ${headers.map(h => {
                  const prop = this.getPropertyFromHeader(h);
                  const val = row[prop];
                  return `<td>${val !== undefined && val !== null ? val : ''}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>`;
    }

    // Create print content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${this.getReportTypeDisplayName()} - ${this.reportPeriod}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; font-size: 11px; padding: 15px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .header-left { flex: 1; }
          .header-right { flex: 1; text-align: right; }
          h1 { font-size: 18px; margin-bottom: 10px; }
          .company-info p, .report-info p { margin: 2px 0; }
          .summary-box { background: #f5f5f5; padding: 10px; margin: 15px 0; border-radius: 4px; }
          .summary-box h3 { font-size: 12px; margin-bottom: 8px; }
          .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
          .summary-item { padding: 5px; }
          .summary-item label { font-size: 9px; color: #666; display: block; }
          .summary-item span { font-size: 12px; font-weight: bold; }
          .summary-item.highlight span { color: #16a34a; }
          .summary-item.cancelled span { color: #ea580c; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 10px; }
          th { background: #f0f0f0; font-weight: bold; }
          .amount { text-align: right; }
          .net { color: #16a34a; font-weight: bold; }
          .date { font-weight: bold; }
          .total { background: #f9fafb; font-weight: bold; }
          .grand-total { background: #e5e7eb; }
          .grand-total td { font-weight: bold; }
          @media print {
            body { padding: 0; }
            @page { margin: 10mm; size: landscape; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <h1>${this.getReportTypeDisplayName()}</h1>
            <div class="company-info">
              <p><strong>Company:</strong> ${this.account?.account_name || '-'}</p>
              <p><strong>Zone:</strong> ${this.selectedZone?.zone_name || 'All Zones'}</p>
              <p><strong>Cashier:</strong> ${this.employeeSelected || 'All Cashiers'}</p>
            </div>
          </div>
          <div class="header-right">
            <div class="report-info">
              <p><strong>Period:</strong> ${this.reportPeriod}</p>
              <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div class="summary-box">
          <h3>SUMMARY</h3>
          <div class="summary-grid">
            <div class="summary-item">
              <label>Total Transactions</label>
              <span>${this.totalTransactions}</span>
            </div>
            <div class="summary-item">
              <label>Total Amount</label>
              <span>₱${this.totalAmount.toFixed(2)}</span>
            </div>
            ${this.totalCanceledCount > 0 ? `
            <div class="summary-item cancelled">
              <label>Cancelled</label>
              <span>${this.totalCanceledCount} (₱${this.totalCancelledAmount.toFixed(2)})</span>
            </div>
            ` : ''}
            ${this.totalUnpaidCount > 0 ? `
            <div class="summary-item">
              <label>Unpaid</label>
              <span>₱${this.totalUnpaidAmount.toFixed(2)} (${this.totalUnpaidCount})</span>
            </div>
            ` : ''}
            ${this.totalRefundedAmount > 0 ? `
            <div class="summary-item">
              <label>Refunded</label>
              <span>₱${this.totalRefundedAmount.toFixed(2)}</span>
            </div>
            ` : ''}
            <div class="summary-item highlight">
              <label>Net Amount (Collected)</label>
              <span>₱${this.totalNetAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        ${tableHtml}
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank', 'width=1000,height=700');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();

      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  }

  /**
   * Pagination: Go to next page
   */
  nextPage() {
    if (this.currentPage < Math.ceil(this.getFilteredData().length / this.itemsPerPage)) {
      this.currentPage++;
    }
  }

  /**
   * Pagination: Go to previous page
   */
  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  /**
   * Helper method for pagination display
   */
  min(a: number, b: number): number {
    return Math.min(a, b);
  }
  
  /**
   * Get total number of pages for pagination (moved to public getter)
   */
  
  /**
   * Sort data by column
   */
  sortData(column: string) {
    if (this.sortColumn === column) {
      // Toggle sort direction if clicking the same column
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    
    // Reset to first page when sorting
    this.currentPage = 1;
  }
  
  /**
   * Get property name from header text
   */
  getPropertyFromHeader(header: string): string {
    // Map header display names to actual property names
    const headerMap = {
      'date': 'date_display',
      'plate number': 'plate_number',
      'zone name': 'zone_name',
      'slot': 'slot_name',
      'vehicle type': 'vehicle_type_custom_name',
      'transaction type': 'transaction_type_name',
      'start': 'time_in_display',
      'started by': 'processedBy',
      'end': 'time_out_display',
      'ended by': 'end_session_by',
      'status': 'exit_status',
      'duration': 'duration',
      'total payment': 'total_payment',
      'initial rate': 'initial_payment',
      'initial rate received by': 'initial_payment_recieved_by',
      'initial rate received date': 'initial_payment_received_date_display',
      'additional payment': 'additional_payment',
      'additional payment received by': 'additional_payment_recieved_by',
      'additional payment received date': 'additional_payment_received_date_display',
      'refund amount': 'refund_amount',
      'refund info': 'refund_info',
      'cancel amount': 'cancel_amount',
      'cancel info': 'cancel_info',
      'discount savings': 'discount_savings',
      'discounted amount': 'discounted_amount',
      'discount details': 'discount_details',
      'net amount': 'net_amount'
    };
    
    const lowerHeader = header.toLowerCase();
    return headerMap[lowerHeader] || lowerHeader;
  }
  
  /**
   * Column Visibility Methods
   */
  
  /**
   * Check if a column should be visible
   */
  isColumnVisible(column: string): boolean {
    return this.visibleColumns.has(column);
  }
  
  /**
   * Toggle column visibility
   */
  toggleColumnVisibility(column: string) {
    if (this.visibleColumns.has(column)) {
      this.visibleColumns.delete(column);
    } else {
      this.visibleColumns.add(column);
    }
    this.saveColumnPreferences();
  }
  
  /**
   * Reset to default visible columns
   */
  resetToDefaultColumns() {
    this.visibleColumns.clear();
    this.defaultVisibleColumns.forEach(col => this.visibleColumns.add(col));
    this.saveColumnPreferences();
  }
  
  /**
   * Select all columns
   */
  selectAllColumns() {
    this.visibleColumns.clear();
    this.allColumns.forEach(col => this.visibleColumns.add(col));
    this.saveColumnPreferences();
  }
  
  /**
   * Deselect all columns (except essentials)
   */
  deselectAllColumns() {
    this.visibleColumns.clear();
    // Keep at least Date and Plate Number for usability
    this.visibleColumns.add('Date');
    this.visibleColumns.add('Plate Number');
    this.saveColumnPreferences();
  }
  
  /**
   * Save column preferences to localStorage
   */
  saveColumnPreferences() {
    try {
      const preferences = {
        reportType: this.selectedReportType,
        visibleColumns: Array.from(this.visibleColumns)
      };
      localStorage.setItem('reportColumnPreferences', JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving column preferences:', error);
    }
  }
  
  /**
   * Load column preferences from localStorage
   */
  loadColumnPreferences() {
    try {
      const saved = localStorage.getItem('reportColumnPreferences');
      if (saved) {
        const preferences = JSON.parse(saved);
        // Only apply if same report type
        if (preferences.reportType === this.selectedReportType && preferences.visibleColumns) {
          this.visibleColumns.clear();
          preferences.visibleColumns.forEach(col => {
            // Only add if it's a valid column
            if (this.allColumns.includes(col)) {
              this.visibleColumns.add(col);
            }
          });
          return true;
        }
      }
    } catch (error) {
      console.error('Error loading column preferences:', error);
    }
    return false;
  }
  
  /**
   * Toggle column selector dropdown
   */
  toggleColumnSelector() {
    this.showColumnSelector = !this.showColumnSelector;
  }
  
  /**
   * Get count of visible columns
   */
  get visibleColumnCount(): number {
    return this.visibleColumns.size;
  }


  async onSelectZone(value:any) {
    // Find the selected zone by name
    this.selectedZone = this.account.settings.zones.find(z => z.zone_name.toLowerCase() == value.toLowerCase());
    
    if (this.selectedZone) {
      // Update the form with the selected zone
      this.form.patchValue({
        zone: this.selectedZone.zone_name
      });
      
      // Update employees list for the selected zone
      this.employees = this.selectedZone.employees || [];
      
      // Add "All" option if not present
      let index = this.employees.findIndex(e => e.employee_id == 'all');
      if(index == -1){
        this.employees.push(
          { 
            date_created : null,
            employee_id: 'all',
            first_name: 'All',
            last_name: '',
            position: ''
          }
        );
        
        // Default to "All" employees
        this.form.patchValue({
          employee : 'all'
        });
        this.employeeSelected = 'all';
      }
      
      console.log('Zone selected:', this.selectedZone.zone_name, 'with zone_id:', this.selectedZone.zone_id);
      
      // If we already have filters applied, refresh the data with the new zone
      if (this.form.valid && this.form.value.dateRangeLogsFrom && this.form.value.dateRangeLogsTo) {
        this.fetchAndDisplayData();
      }
    }
  }

  async ngOnInit() {
    // Log page access
    await this.auditService.logNavigation(window.location.pathname, 'Reports - Analytics Dashboard');

    this.form = this.formBuilder.group({
      dateRangeLogsFrom: [''],
      dateRangeLogsTo: [''],
      reportType:['general'], // Set a default report type
      zone:[''],
      employee:['all'], // Default to 'all' employees
    });

    this.selectedReportType = 'general';

    // Subscribe to form control changes to clear active state
    this.form.get('dateRangeLogsFrom')?.valueChanges.subscribe(() => {
      if (!this.isSettingQuickRange && this.activeQuickDateRange) {
        this.onDateInputChange();
      }
    });

    this.form.get('dateRangeLogsTo')?.valueChanges.subscribe(() => {
      if (!this.isSettingQuickRange && this.activeQuickDateRange) {
        this.onDateInputChange();
      }
    });

    if(this.zones.length == 1){
      this.onSelectZone(this.zones[0].zone_name);
    }
    
    
  }

  async changeEmployee(employeeId: any) {
    this.employeeSelected = employeeId;
  }

  async onSelectReportType(value:any){
    this.selectedReportType = value;
    
    // Reset table display
    this.showResultsTable = false;
    this.tableData = [];
    this.tableHeaders = [];
    this.summaryHeaders = [];
    this.occupancyDates = [];
    this.totalTransactions = 0;
    this.totalAmount = 0;
    
    // Reset employee selection when changing report types for consistency
    if (this.form) {
      this.form.patchValue({
        employee: 'all'
      });
      this.employeeSelected = 'all';
    }
    
    if(this.userAuth.roles.superAdmin){
      this.employees = this.allEmployees;
    }
    
    console.log('Report type changed to:', this.selectedReportType);
  }

  async printLogs(action,type){

    if(this.selectedReportType !== 'general'){

      if(this.zones.length === 1){
        this.form.value.zone = this.zones[0].zone_name;
      }

      if(this.form.value.employee == '' || this.form.value.reportType == '' || this.form.value.zone == ''){
        await Swal.fire({
          title: 'Ooops!',
          html: 'Please select employee, zone and report type its required fields.',
          allowEscapeKey: true,
          allowOutsideClick: true,
          showConfirmButton: false,
          showCancelButton: true,
          showCloseButton: true,
          cancelButtonText: 'Ok',
          icon: 'warning',
        });
        return
      }
    }

    // Validate date range
    const startDate = this.form.value.dateRangeLogsFrom;
    const endDate = this.form.value.dateRangeLogsTo;
    
    if (!startDate || !endDate) {
      await Swal.fire({
        title: 'Ooops!',
        html: 'Please select both start date and end date.',
        allowEscapeKey: true,
        allowOutsideClick: true,
        showConfirmButton: false,
        showCancelButton: true,
        showCloseButton: true,
        cancelButtonText: 'Ok',
        icon: 'warning',
      });
      return;
    }

    // Validate date order
    if (new Date(startDate) > new Date(endDate)) {
      await Swal.fire({
        title: 'Ooops!',
        html: 'Start date cannot be later than end date.',
        allowEscapeKey: true,
        allowOutsideClick: true,
        showConfirmButton: false,
        showCancelButton: true,
        showCloseButton: true,
        cancelButtonText: 'Ok',
        icon: 'warning',
      });
      return;
    }

    // Validate date range - maximum 1 month
    const dateRangeValidation = this.validateDateRange(new Date(startDate), new Date(endDate));
    if (!dateRangeValidation.isValid) {
      await Swal.fire({
        title: 'Date Range Too Long!',
        html: dateRangeValidation.message,
        allowEscapeKey: true,
        allowOutsideClick: true,
        showConfirmButton: false,
        showCancelButton: true,
        showCloseButton: true,
        cancelButtonText: 'Ok',
        icon: 'warning',
      });
      return;
    }

    if(this.employeeSelected !== ''){
      this.form.value.employee = this.employeeSelected;
    }

    if(this.form.valid){
      this.print(action, type, startDate, endDate);
    }
  }

  async print(action, type, dateFrom, dateTo) {
    let start = new Date(dateFrom);
    let end = new Date(dateTo);
    if (this.auth.canEdit(this.userAuth)) {

      if (action === 'logs') {

        if (start && !end) {
          await Swal.fire({
            title: 'Ooops!',
            html: 'Please select date range type and dates (from date & to date)',
            allowEscapeKey: true,
            allowOutsideClick: true,
            showConfirmButton: false,
            showCancelButton: true,
            showCloseButton: true,
            cancelButtonText: 'Ok',
            icon: 'warning',
          });
          return
        }

      }

      await Swal.fire({
        title: 'Generating reports... Please wait.',
        allowEscapeKey: false,
        allowOutsideClick: false,
        showConfirmButton: false,
        showCancelButton: false,
        showCloseButton: false,
        timerProgressBar: true,
        didOpen: () => {
          Swal.showLoading();
          //this.loadDataTable();
          if (type === "csv") {
            this.getLogsDataForCSV(start,end)
          } else if (type === "pdf") {
            // PDF generation handled through the same method
            this.getLogsDataForCSV(start,end);
          }
        }
      });

    } else {
      await Swal.fire({
        title: 'Ooops!',
        html: 'You have no rights to generate reports. Please contact your administrator. Thank you!',
        allowEscapeKey: true,
        allowOutsideClick: true,
        showConfirmButton: false,
        showCancelButton: true,
        showCloseButton: true,
        cancelButtonText: 'Ok',
        icon: 'warning',
      });
    }

  }

  private pushStringAtIndex(arr, index, str) {
    if (index < 0) {
      throw new Error("Invalid index provided.");
    }
    const diff = index - arr.length + 1;
    if (diff > 0) {
      arr.push(...Array(diff).fill(''));
    }

    arr.splice(index, 0, str);
  }

  private getTotalTransaction(dataSet: any): string {
    let reArrageCount:any[] = [];
    let grandTotal: number = 0;
    dataSet.headers.values.forEach((value) => {
      grandTotal += value.count;
      this.pushStringAtIndex(reArrageCount, value.indexHeaderLabel, value.count);
    });


    reArrageCount = reArrageCount.filter(item => item !== '');
    let stringCount = reArrageCount.join(',');
    return stringCount+','+ grandTotal;
  }

  private getTotalSales(dataSet: any): string{
    let reArrageSales:any[] = [];
    let grandTotal: number = 0;
    dataSet.headers.values.forEach((value) => {
      grandTotal += value.sales;
      this.pushStringAtIndex(reArrageSales, value.indexHeaderLabel, value.sales);
    });

    reArrageSales = reArrageSales.filter(item => item !== '');
    let stringSales = reArrageSales.join(',');
    return stringSales+','+ grandTotal;
  }


  private getGroupCsvRowsAverageParkingDuration(dataSet: any, numberOfVehicles:any): string {
    let reArrageAverageParkingDuration:any[] = [];
    dataSet.headers.values.forEach((value) => {
      let timeInHours = value.time_duration_minute / 60;
      let averageTimeInHours = (timeInHours / value.count).toFixed(2);
      this.pushStringAtIndex(reArrageAverageParkingDuration, value.indexHeaderLabel, averageTimeInHours);
    });

    reArrageAverageParkingDuration = reArrageAverageParkingDuration.filter(item => item !== '');

    let stringSales = reArrageAverageParkingDuration.join(',');
    return stringSales;
  }

  private getGroupRowAverageParkingOccupancy(dataSet:any){

    let occupancyPercentage = [];
    for(let occupancy of dataSet.timeOccupancyData){
      occupancyPercentage.push(occupancy.occupancyPercentage);
    }
    return occupancyPercentage.join(',');

  }

  private processTransactions(transactionRecords:any, type:string ){

    let totalTransaction :number = 0;

    let data:any[] = [];

    let headersLabels:any[] = [];

    let indexHeaderLabel:any;

    for(let key in transactionRecords){

      let name = transactionRecords[key].processedBy;

      if(headersLabels.length == 0){
        headersLabels.push(name);
      }else{
        let index = headersLabels.findIndex(hl=> hl == name);
        if(index == -1){
          headersLabels.push(name);
        }
      }

      indexHeaderLabel = headersLabels.findIndex(hl => hl == transactionRecords[key].processedBy);

      totalTransaction += transactionRecords[key].count;

      let value = {
        count: transactionRecords[key].count,
        sales : transactionRecords[key].total_payment,
        name: name,
        indexHeaderLabel: indexHeaderLabel,
        date: moment(transactionRecords[key].formattedDate).format("MMM DD YYYY"),
      }

      if(data.length == 0){
        data.push(
          {
            date: moment(transactionRecords[key].formattedDate).format("MMM DD YYYY"),
            headers:
              {
                values: [value]
              }

          }
        )

      }else{
        let indexDate = data.findIndex(d => d.date == moment(transactionRecords[key].formattedDate).format("MMM DD YYYY"))
        if(indexDate > -1){
          let indexName =  data[indexDate]['headers']['values'].findIndex(h => h.name == transactionRecords[key].name);
          if(indexName > -1){
            data[indexDate]['headers']['values'][indexName].count += transactionRecords[key].count;
            data[indexDate]['headers']['values'][indexName].sales += transactionRecords[key].total_payment;

          }else{
            data[indexDate]['headers']['values'].push(value)
          }
        }else{
          data.push(
            {
              date: moment(transactionRecords[key].formattedDate).format("MMM DD YYYY"),
              headers:
                {
                  values: [value]
                }
            }
          )
        }
      }
    }
    for(let unfilteredData of data){
      let values = unfilteredData['headers']['values'];

      for (let index = 0; index < headersLabels.length; index++) {
        const label = headersLabels[index];
        let labelIndex = values.findIndex(v=> v['name'].toLowerCase() == label.toLowerCase())
        if(labelIndex == -1){
          values.push({
            count: 0,
            sales : 0,
            name: label,
            indexHeaderLabel: index,
            date: unfilteredData.date,
          })
        }
      }



  }
    return {
      "totalTransaction": totalTransaction,
      "dataTransaction" : data,
      "headerTransaction": headersLabels
    };

  }

  private processSales(salesRecords:any,type:any){

    let totalTransaction :number = 0;
    let totalSales:number = 0;

    let data:any[] = [];

    let headersLabels:any[] = [];

    let indexHeaderLabel:any;

    for(let key in salesRecords){
      if(salesRecords[key].name != ""){
        let name = salesRecords[key].name;

        if(headersLabels.length == 0){
          headersLabels.push(name);
        }else{
          let index = headersLabels.findIndex(hl=> hl == name);
          if(index == -1){
            headersLabels.push(name);
          }
        }

        indexHeaderLabel = headersLabels.findIndex(hl => hl == salesRecords[key].name);

        totalTransaction += salesRecords[key].count;
        totalSales += salesRecords[key].total_payment_amount;

        let value = {
          count: salesRecords[key].count,
          sales : salesRecords[key].total_payment_amount,
          name: name,
          indexHeaderLabel: indexHeaderLabel,
          date: moment(salesRecords[key].formattedDate).format("MMM DD YYYY"),
        }

        if(data.length == 0){
          data.push(
            {
              date: moment(salesRecords[key].formattedDate).format("MMM DD YYYY"),
              headers:
                {
                  values: [value]
                }

            }
          )

        }else{
          let indexDate = data.findIndex(d => d.date == moment(salesRecords[key].formattedDate).format("MMM DD YYYY"))
          if(indexDate > -1){
            let indexName =  data[indexDate]['headers']['values'].findIndex(h => h.name == salesRecords[key].name);
            if(indexName > -1){
              data[indexDate]['headers']['values'][indexName].count += salesRecords[key].count;
              data[indexDate]['headers']['values'][indexName].sales += salesRecords[key].total_payment_amount;

            }else{
              data[indexDate]['headers']['values'].push(value)
            }
          }else{
            data.push(
              {
                date: moment(salesRecords[key].formattedDate).format("MMM DD YYYY"),
                headers:
                  {
                    values: [value]
                  }
              }
            )
          }
        }
      }

    }


    for(let unfilteredData of data){
      let values = unfilteredData['headers']['values'];

      for (let index = 0; index < headersLabels.length; index++) {
        const label = headersLabels[index];
        let labelIndex = values.findIndex(v=> v['name'].toLowerCase() == label.toLowerCase())
        if(labelIndex == -1){
          values.push({
            count: 0,
            sales : 0,
            name: label,
            indexHeaderLabel: index,
            date: unfilteredData.date,
          })
        }
      }
    }

    return {
      "totalSales": totalSales,
      "dataSales" : data,
      "headerSales": headersLabels
    };
  }

  async processVehicleTypeTransaction(vehicleTypeRecords:any){

    let totalVehicleTypeTransaction :number = 0;
    let totalSales:number = 0;

    let data:any[] = [];
    let headersLabels:any[] = [];
    let indexHeaderLabel:any;

    for(let key in vehicleTypeRecords){
      // Fix vehicle type name handling - don't modify vehicle type names
      let name = vehicleTypeRecords[key].vehicle_type_custom_name;

      if(headersLabels.length == 0){
        headersLabels.push(name);
      }else{
        let index = headersLabels.findIndex(hl=> hl == name);
        if(index == -1){
          headersLabels.push(name);
        }
      }
      indexHeaderLabel = headersLabels.findIndex(hl => hl == vehicleTypeRecords[key].vehicle_type_custom_name);

      if(indexHeaderLabel > -1){

        totalVehicleTypeTransaction += vehicleTypeRecords[key].count;
        totalSales += vehicleTypeRecords[key].total_payment_amount;

        let value = {
          count: vehicleTypeRecords[key].count,
          sales: vehicleTypeRecords[key].total_payment_amount,
          name: name,
          indexHeaderLabel: indexHeaderLabel,
          date: moment(vehicleTypeRecords[key].formattedDate).format("MMM DD YYYY"),
        }

        if(data.length == 0){
          data.push(
            {
              date: moment(vehicleTypeRecords[key].formattedDate).format("MMM DD YYYY"),
              headers:
                {
                  values: [value]
                }

            }
          )

        }else{
          let indexDate = data.findIndex(d => d.date == moment(vehicleTypeRecords[key].formattedDate).format("MMM DD YYYY"))
          if(indexDate > -1){

            let indexName =  data[indexDate]['headers']['values'].findIndex(h => h.name == vehicleTypeRecords[key].vehicle_type_custom_name);

            if(indexName > -1){
              data[indexDate]['headers']['values'][indexName].count += vehicleTypeRecords[key].count;
              data[indexDate]['headers']['values'][indexName].sales += vehicleTypeRecords[key].total_payment_amount;

            }else{
              data[indexDate]['headers']['values'].push(value)
            }
          }else{
            data.push(
              {
                date: moment(vehicleTypeRecords[key].formattedDate).format("MMM DD YYYY"),
                headers:
                  {
                    values: [value]
                  }
              }
            )
          }
        }
      }
    }

    // Ensure all vehicle types are represented for each date
    for(let unfilteredData of data){
      let values = unfilteredData['headers']['values'];

      for (let index = 0; index < headersLabels.length; index++) {
        const label = headersLabels[index];
        let labelIndex = values.findIndex(v=> v['name'] == label)
        if(labelIndex == -1){
          values.push({
            count: 0,
            sales : 0,
            name: label,
            indexHeaderLabel: index,
            date: unfilteredData.date,
          })
        }
      }
    }
    
    return {
      "totalVehicleTypeTransaction": totalVehicleTypeTransaction,
      "dataVehicleTypeTransaction" : data,
      "headersLabel": headersLabels
    };
  }

  private processAverageDurationPerTransactionType(averageDurationPerTransactionTypeRecords:any){


    let totalAverageDurationPerTransactionType :number = 0;

    let data:any[] = [];

    let headersLabels:any[] = [];

    let indexHeaderLabel:any;

    for(let key in averageDurationPerTransactionTypeRecords){

      let name = averageDurationPerTransactionTypeRecords[key].vehicle_type_custom_name;

      if(headersLabels.length == 0){
        headersLabels.push(name);
      }else{
        let index = headersLabels.findIndex(hl=> hl == name);
        if(index == -1){
          headersLabels.push(name);
        }
      }

      indexHeaderLabel = headersLabels.findIndex(hl => hl == averageDurationPerTransactionTypeRecords[key].vehicle_type_custom_name);

      totalAverageDurationPerTransactionType += averageDurationPerTransactionTypeRecords[key].count;

      let value = {
        count: averageDurationPerTransactionTypeRecords[key].count,
        time_duration_minute : averageDurationPerTransactionTypeRecords[key].time_duration_minute,
        name: name,
        indexHeaderLabel: indexHeaderLabel,
        date: moment(averageDurationPerTransactionTypeRecords[key].formattedDate).format("MMM DD YYYY"),
      }

      if(data.length == 0){
        data.push(
          {
            date: moment(averageDurationPerTransactionTypeRecords[key].formattedDate).format("MMM DD YYYY"),
            headers:
              {
                values: [value]
              }

          }
        )

      }else{
        let indexDate = data.findIndex(d => d.date == moment(averageDurationPerTransactionTypeRecords[key].formattedDate).format("MMM DD YYYY"))
        if(indexDate > -1){
          let indexName =  data[indexDate]['headers']['values'].findIndex(h => h.name == averageDurationPerTransactionTypeRecords[key].name);
          if(indexName > -1){
            data[indexDate]['headers']['values'][indexName].count += averageDurationPerTransactionTypeRecords[key].count;
            data[indexDate]['headers']['values'][indexName].time_duration_minute += averageDurationPerTransactionTypeRecords[key].time_duration_minute;

          }else{
            data[indexDate]['headers']['values'].push(value)
          }
        }else{
          data.push(
            {
              date: moment(averageDurationPerTransactionTypeRecords[key].formattedDate).format("MMM DD YYYY"),
              headers:
                {
                  values: [value]
                }
            }
          )
        }
      }
    }
    for(let unfilteredData of data){
      let values = unfilteredData['headers']['values'];

      for (let index = 0; index < headersLabels.length; index++) {
        const label = headersLabels[index];
        let labelIndex = values.findIndex(v=> v['name'].toLowerCase() == label.toLowerCase())
        if(labelIndex == -1){
          values.push({
            count: 0,
            time_duration_minute : 0,
            name: label,
            indexHeaderLabel: index,
            date: unfilteredData.date,
          })
        }
      }



  }
    return {
      "totalAverageDurationPerTransactionType": totalAverageDurationPerTransactionType,
      "dataTotalAverageDurationPerTransactionType" : data,
      "headersLabel": headersLabels
    };


  }

  async calculate(logs:any, type: string) {

    let transactionRecords:any[] = [];
    let salesRecords:any[] = [];
    let transactionVehicleTypeRecords:any[] = [];
    let averageDurationPerTransactionTypeRecords :any[] = [];

    let transactionData:any
    let salesData:any;
    let transactionVehicleTypeData: any;
    let averageDurationPerTransactionTypeData: any;
    for (const entry of logs) {
      // if(entry.initial_payment != ""){
        const { updatedAt,
                total_payment,
                initial_payment,
                amount_paid,
                initial_payment_recieved_by_id,
                initial_payment_recieved_by,
                additional_payment,
                additional_payment_recieved_by,
                additional_payment_recieved_by_id,
                vehicle_type_custom_name,
                time_duration_hour,
                time_duration_minute,
                processedBy,
                plate_number,
                fullDateTimeStamp,
                paidBy_id,
                processedBy_id,
                paid
              } = entry;

        switch (type) {
          case 'dsa':

              //For Transaction Data
              if(processedBy !== ""){

                let formattedDate = moment(fullDateTimeStamp).format('MM/DD/YYYY')
                let nameDateIndex = transactionRecords.
                findIndex(r => r.formattedDate == moment(entry.fullDateTimeStamp).format('MM/DD/YYYY')
                &&  r.processedBy == entry.processedBy)

                if ( transactionRecords.length > 0) {
                  if(nameDateIndex > -1){
                    transactionRecords[nameDateIndex].total_payment += total_payment;
                    transactionRecords[nameDateIndex].count += 1;
                  }else{
                    transactionRecords.push({
                      formattedDate,
                      total_payment,
                      count: 1,
                      initial_payment_recieved_by_id,
                      initial_payment_recieved_by,
                      vehicle_type_custom_name,
                      time_duration_hour,
                      time_duration_minute,
                      processedBy,
                      fullDateTimeStamp,
                      additional_payment_recieved_by_id,
                      paidBy_id,
                      processedBy_id
                    });
                  }

                } else {
                  transactionRecords.push({
                    formattedDate,
                    total_payment,
                    count: 1,
                    initial_payment_recieved_by_id,
                    initial_payment_recieved_by,
                    vehicle_type_custom_name,
                    time_duration_hour,
                    time_duration_minute,
                    processedBy,
                    fullDateTimeStamp,
                    additional_payment_recieved_by_id,
                    paidBy_id,
                    processedBy_id
                  });
                }

              }

              //For Sales Data
              let formattedDateSales = moment(fullDateTimeStamp).format('MM/DD/YYYY')

              if (salesRecords.length > 0) {

                if(initial_payment_recieved_by != additional_payment_recieved_by && additional_payment_recieved_by == ""){
                  let initialPaymentRecievedbyOnlyIndex = salesRecords.findIndex(r => r.formattedDate == moment(entry.fullDateTimeStamp).format('MM/DD/YYYY') && entry.initial_payment_recieved_by == r.name);
                  if(initialPaymentRecievedbyOnlyIndex > -1){
                    salesRecords[initialPaymentRecievedbyOnlyIndex].total_payment_amount += amount_paid;
                    salesRecords[initialPaymentRecievedbyOnlyIndex].count += 1;
                  }else{
                    salesRecords.push({
                      formattedDate: formattedDateSales,
                      total_payment_amount: amount_paid,
                      count: 1,
                      name: initial_payment_recieved_by,
                      });
                  }
                }

                if(initial_payment_recieved_by == additional_payment_recieved_by){

                  let sameUserIndex = salesRecords.
                  findIndex(r => r.formattedDate == moment(entry.fullDateTimeStamp).format('MM/DD/YYYY') && entry.initial_payment_recieved_by == r.name);
                  if(sameUserIndex > -1){
                    salesRecords[sameUserIndex].total_payment_amount += total_payment;
                    salesRecords[sameUserIndex].count += 1;
                  }else{
                    salesRecords.push({
                      formattedDate: formattedDateSales,
                      total_payment_amount: amount_paid + additional_payment,
                      count: 1,
                      name: initial_payment_recieved_by,
                    });
                  }

                }

                if(initial_payment_recieved_by != additional_payment_recieved_by && additional_payment_recieved_by != ""){

                  //INITAIL
                  let initialUserIndex = salesRecords.findIndex(r => r.formattedDate == moment(entry.fullDateTimeStamp).format('MM/DD/YYYY') && entry.initial_payment_recieved_by == r.name);
                  if(initialUserIndex > -1){
                    salesRecords[initialUserIndex].total_payment_amount += amount_paid;
                    salesRecords[initialUserIndex].count += 1;
                  }else{
                    salesRecords.push({
                      formattedDate: formattedDateSales,
                      total_payment_amount: amount_paid,
                      count: 1,
                      name: initial_payment_recieved_by,
                      });
                  }


                  //ADDITONAL
                  let additionalUserIndex = salesRecords.findIndex(r => r.formattedDate == moment(entry.fullDateTimeStamp).format('MM/DD/YYYY') && entry.additional_payment_recieved_by == r.name);
                  if(additionalUserIndex > -1){
                    salesRecords[additionalUserIndex].total_payment_amount += additional_payment;
                    salesRecords[additionalUserIndex].count += 1;
                  }else{
                    salesRecords.push({
                      formattedDate: formattedDateSales,
                      total_payment_amount: additional_payment,
                      count: 1,
                      name: additional_payment_recieved_by,
                    });
                  }
                }

              } else {

                if(initial_payment_recieved_by != additional_payment_recieved_by && additional_payment_recieved_by == ""){

                  salesRecords.push({
                    formattedDate: formattedDateSales,
                    total_payment_amount: amount_paid,
                    count: 1,
                    name: initial_payment_recieved_by,
                    });
                }

                if(initial_payment_recieved_by == additional_payment_recieved_by){
                  salesRecords.push({
                    formattedDate: formattedDateSales,
                    total_payment_amount: total_payment,
                    count: 1,
                    name: initial_payment_recieved_by,
                  });
                }

                if(initial_payment_recieved_by != additional_payment_recieved_by && additional_payment_recieved_by != ""){
                  //INITIAL
                  salesRecords.push({
                    formattedDate: formattedDateSales,
                    total_payment_amount: amount_paid,
                    count: 1,
                    name: initial_payment_recieved_by,
                    });

                  //ADDITIONAL
                  salesRecords.push({
                    formattedDate: formattedDateSales,
                    total_payment_amount: additional_payment,
                    count: 1,
                    name: additional_payment_recieved_by,
                  });
                }

              }
            break;
          case 'dstt':

            let formattedDate = moment(fullDateTimeStamp).format('MM/DD/YYYY')

              let sanitizeVehicleName = entry.vehicle_type_custom_name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

              let nameDateIndex:any;
              nameDateIndex = transactionVehicleTypeRecords.findIndex(r => r.formattedDate == moment(entry.fullDateTimeStamp).format('MM/DD/YYYY')
              &&  r.vehicle_type_custom_name == entry.sanitizeVehicleName)

              if(transactionVehicleTypeRecords.length > 0){
                if(nameDateIndex > -1){
                  transactionVehicleTypeRecords[nameDateIndex].count += 1;
                  transactionVehicleTypeRecords[nameDateIndex].total_payment_amount += additional_payment + amount_paid;
                }else{
                  transactionVehicleTypeRecords.push({ formattedDate, total_payment_amount : additional_payment + amount_paid, count: 1, initial_payment_recieved_by_id, initial_payment_recieved_by, additional_payment, vehicle_type_custom_name: sanitizeVehicleName, time_duration_hour, time_duration_minute, processedBy, fullDateTimeStamp });
                }
              } else {
                transactionVehicleTypeRecords.push({ formattedDate, total_payment_amount : additional_payment + amount_paid , count: 1, initial_payment_recieved_by_id, initial_payment_recieved_by, additional_payment, vehicle_type_custom_name: sanitizeVehicleName, time_duration_hour, time_duration_minute,  processedBy, fullDateTimeStamp });
              }
              break;
          case 'apdtt':
                if(processedBy !== ""){

                  let formattedDate = moment(fullDateTimeStamp).format('MM/DD/YYYY')
                  let nameDateIndex = averageDurationPerTransactionTypeRecords.
                  findIndex(r => r.formattedDate == moment(entry.fullDateTimeStamp).format('MM/DD/YYYY')
                  &&  r.vehicle_type_custom_name == entry.vehicle_type_custom_name)

                  if ( averageDurationPerTransactionTypeRecords.length > 0) {
                    if(nameDateIndex > -1){
                      let hoursInMinute = time_duration_hour * 60;
                      let totalMinutes =  hoursInMinute + time_duration_minute
                      averageDurationPerTransactionTypeRecords[nameDateIndex].count += 1;
                      averageDurationPerTransactionTypeRecords[nameDateIndex].time_duration_minute += totalMinutes;
                    }else{

                      let hoursInMinute = time_duration_hour * 60;
                      let totalMinutes =  hoursInMinute + time_duration_minute

                      let initialObjt = {
                        formattedDate,
                        total_payment,
                        count: 1,
                        initial_payment_recieved_by_id,
                        initial_payment_recieved_by,
                        vehicle_type_custom_name,
                        time_duration_hour,
                        time_duration_minute: totalMinutes,
                        processedBy,
                        fullDateTimeStamp
                      }

                      averageDurationPerTransactionTypeRecords.push(initialObjt);
                    }

                  } else {
                    let hoursInMinute = time_duration_hour * 60;
                    let totalMinutes =  hoursInMinute + time_duration_minute

                    let initialObjt = {
                      formattedDate,
                      total_payment,
                      count: 1,
                      initial_payment_recieved_by_id,
                      initial_payment_recieved_by,
                      vehicle_type_custom_name,
                      time_duration_hour,
                      time_duration_minute: totalMinutes,
                      processedBy,
                      fullDateTimeStamp
                    }

                    averageDurationPerTransactionTypeRecords.push(initialObjt);
                  }

                }
          break;
          default:
            break;
        }
      // }
    }

    switch (type) {
      case 'dsa':
          transactionData = this.processTransactions(transactionRecords,type);
          salesData = this.processSales(salesRecords,type)
        break;
      case 'dstt':
          transactionVehicleTypeData = this.processVehicleTypeTransaction(transactionVehicleTypeRecords)
        break;
        case 'apdtt':
          averageDurationPerTransactionTypeData = this.processAverageDurationPerTransactionType(averageDurationPerTransactionTypeRecords)
        break;
      default:
        break;
    }

    return {
      transaction : transactionData,
      sales:salesData,
      vehicleTypeTransaction: transactionVehicleTypeData,
      averageDurationPerTransactionType: averageDurationPerTransactionTypeData,
    };

  }

  async filterOccupancyLogsPerDate(logs:any){

    let records:any[] = [];

    for(var log of logs){

      let formattedDate = moment(log.updatedAt).format('MMM DD YYYY')

      if(records.length == 0){
        records.push({
          date: formattedDate,
          vehichelActivity : [log],
          vehicleEntry: [],
          vehicleExit: [],
          timeOccupancyData: []
        })
      }else{
        let indexByDate = records.findIndex(r=> r.date == formattedDate);
        if(indexByDate > -1 && records[indexByDate].date == formattedDate){
          records[indexByDate]['vehichelActivity'].push(log);
        }else{
          records.push({
            date: formattedDate,
            vehichelActivity : [log],
            vehicleEntry: [],
            vehicleExit: [],
            timeOccupancyData: []
          })
        }

      }

    }

    return records;

  }

  async setOccupancyLogPerTimeOfDate(records:any){

    for(let recordLogs of records){

      for(let log of recordLogs.vehichelActivity){

        let formattedDate = moment(log.updatedAt).format('MMM DD YYYY');

        if(formattedDate == recordLogs.date){

          let entry = (log.entry_time?.seconds * 1000);

          let dateTimeInHour = moment(entry).format('hh');
          let dateTimeInMeridiem = moment(entry).format('A');
          let timeIn = dateTimeInHour+':00 '+dateTimeInMeridiem;

          if(recordLogs.vehicleEntry.length == 0 ){
            recordLogs.vehicleEntry.push({
              time: timeIn,
              entry: 1,
              activeSession: 0,
              occupancyPercentage: ''

            })
          }else{
            let indexVeicleEntry = recordLogs.vehicleEntry.findIndex(rv=> rv.time == timeIn)
            if(indexVeicleEntry > -1){
              recordLogs.vehicleEntry[indexVeicleEntry].entry += 1;
            }else{
              recordLogs.vehicleEntry.push({
                time: timeIn,
                entry: 1,
                activeSession: 0,
                occupancyPercentage: ''

              })
            }
          }


          let exit = (log.exit_time?.seconds * 1000);

          let dateTimeOutHour = moment(exit).format('hh');
          let dateTimeOutMeridiem = moment(exit).format('A');
          let timeOut = dateTimeOutHour+':00 '+dateTimeOutMeridiem;

          if(recordLogs.vehicleExit.length == 0 ){
            recordLogs.vehicleExit.push({
              time: timeOut,
              exit: 1,
              activeSession: 0,
              occupancyPercentage: ''

            })
          }else{
            let indexVeicleExit = recordLogs.vehicleExit.findIndex(rv=> rv.time == timeOut)
            if(indexVeicleExit > -1){
              recordLogs.vehicleExit[indexVeicleExit].exit += 1;
            }else{
              recordLogs.vehicleExit.push({
                time: timeOut,
                exit: 1,
                activeSession: 0,
                occupancyPercentage: ''

              })
            }
          }

        }
      }

      recordLogs.timeOccupancyData = await this.mergeDateTimeOccupancyData(recordLogs.vehicleEntry, recordLogs.vehicleExit);

    }


    return {
      filteredData : records,
      headersLabel:TemplateOccupancyTimeHeader,
    };

  }

  async mergeDateTimeOccupancyData (entry:any, exit:any){
    let headerLabels:any =  TemplateOccupancyTimeHeader;

    // Fill in missing time slots
    for(let template of TemplateOccupancyTimeRage){
      let indexEntry = entry.findIndex(e=> e.time == template.time);
      if(indexEntry == -1){
        entry.push({
          time: template.time,
          entry: 0,
          activeSession: 0,
          occupancyPercentage: ''
        });
      }

      let indexExit = exit.findIndex(e=> e.time == template.time);
      if(indexExit == -1){
        exit.push({
          time: template.time,
          exit: 0,
          activeSession: 0,
          occupancyPercentage: ''
        });
      }
    }

    // Merge entry and exit data
    let mergeDateTimeOccupancyDataResult = entry.map(item1 => {
      const correspondingItem = exit.find(item2 => item2.time === item1.time);
      if (correspondingItem) {
        return { ...item1, ...correspondingItem };
      }
      return item1;
    });

    // Sort by time
    mergeDateTimeOccupancyDataResult.sort((a, b) => {
      const timeA = new Date(`2023-01-01 ${a.time}`).getTime();
      const timeB = new Date(`2023-01-01 ${b.time}`).getTime();
      return timeA - timeB;
    });

    // Calculate active sessions and occupancy percentage
    let carryOverActiveSession = 0;
    for (let index = 0; index < mergeDateTimeOccupancyDataResult.length; index++) {
      let item = mergeDateTimeOccupancyDataResult[index];
      let entryCount = item.entry || 0;
      let exitCount = item.exit || 0;
      
      // Calculate active sessions taking into account previous timeslot
      let activeSession = carryOverActiveSession + entryCount - exitCount;
      mergeDateTimeOccupancyDataResult[index].activeSession = activeSession;
      carryOverActiveSession = activeSession;
      
      // Calculate percentage based on zone slots
      let zoneSlots = this.selectedZone?.zone_slots || 1; // Prevent division by zero
      let occupancyPercentage = (activeSession / zoneSlots) * 100;
      
      // Format occupancy percentage with proper bounds
      occupancyPercentage = Math.max(0, Math.min(100, occupancyPercentage));
      mergeDateTimeOccupancyDataResult[index].occupancyPercentage = occupancyPercentage.toFixed(2).toString() + '%';
    }

    return mergeDateTimeOccupancyDataResult;
  }

  async getLogsDataForCSV(start, end) {
    // Initialize arrays for data collection
    let arrayData = [];
    let totalFeeCollected = 0;
    let startPeriodCovered = this.datePipe.transform(start, 'shortDate');
    let endPeriodCovered = this.datePipe.transform(end, 'shortDate');
    
    let logs:any;

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    if(this.selectedReportType == 'general'){
        logs = await firebase.firestore().collection('logs')
        .where('account_key', '==', this.account.account_key)
        .where('time_in', '>=', start)
        .where('time_in', '<=', end).get();
    }else if(this.selectedReportType != 'general' && this.selectedZone != undefined){
      logs = await firebase.firestore().collection('logs')
      .where('account_key', '==', this.account.account_key)
      .where('zone_id','==', this.selectedZone.zone_id)
      .where('time_in', '>=', start)
      .where('time_in', '<=', end).get();
    }

    if(logs.empty) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'No data found.',
      })
      return
    }

    let dataLogs;
    let arrayLogs = [];

    let logsMonitoring = {
      account_key: this.account.account_key,
      timestamp : new Date(),
      total_count : logs.docs.length,
      type :  this.selectedReportType + ' - csv' ,
      user_id : this.userAuth.uid
    }

    await this.logsService.addLogsMonitoring(logsMonitoring);

    for (let log of logs.docs) {

      let duration;
      let parkingFee;
      let time_duration_hour;
      let time_duration_minute;
      let time_entry_date_format;
      let time_exit_date_format;

      dataLogs = log.data();

      if(this.employeeSelected !== '' && this.employeeSelected !== 'all'){

        if(dataLogs.additional_payment_recieved_by_id !== this.employeeSelected &&
          dataLogs.initial_payment_recieved_by_id !== this.employeeSelected &&
          dataLogs.paidBy_id !== this.employeeSelected
          ){
            continue;
          }

      }

      if(dataLogs.initial_payment_recieved_date != null){
        let initailPaymentseconds = (dataLogs.initial_payment_recieved_date.seconds * 1000);
        dataLogs.initial_payment_recieved_date = moment(initailPaymentseconds).format("MM/DD/YYYY h:mm A");
      }else{
        dataLogs.initial_payment_recieved_date = "";
      }

      if(dataLogs.additional_payment_recieved_date != null){
        let initailPaymentseconds = (dataLogs.additional_payment_recieved_date.seconds * 1000);
        dataLogs.additional_payment_recieved_date = moment(initailPaymentseconds).format("MM/DD/YYYY h:mm A");
      }else{
        dataLogs.additional_payment_recieved_date = "";
      }

      let fullDateTimeStamp = new Date(dataLogs.time_in.seconds * 1000)

      let timeIn = (dataLogs.time_in.seconds * 1000);
      let dateTimeIn = moment(timeIn).format('MM/DD/YYYY hh:mm A'); //full format: L hh:mm A
      let dateTimeOut = "";

      if (dataLogs.time_out) {

        if(dataLogs.actual_end_time == undefined){
          let timeOut = (dataLogs.time_out.seconds * 1000);
          dateTimeOut = moment(timeOut).format('MM/DD/YYYY hh:mm A'); //full format: L hh:mm A
        }else{
          let actualEndTime = (dataLogs.actual_end_time.seconds * 1000);
          dateTimeOut = moment(actualEndTime).format('MM/DD/YYYY hh:mm A'); //full format: L hh:mm A
        }

      } else {
        dateTimeOut = "incomplete";

      }

      if (dataLogs.time_in.seconds && dataLogs.time_out?.seconds) {

        time_entry_date_format = dataLogs.time_in;

        if(dataLogs.actual_end_time == undefined){
          time_exit_date_format = dataLogs.time_out;
        }else{
          time_exit_date_format = dataLogs.actual_end_time;
        }

        let startTime = moment(moment.unix(dataLogs.time_in?.seconds), "HH:mm:ss a");

        let endTime :any;
        if(dataLogs.actual_end_time == undefined){
          endTime = moment(moment.unix(dataLogs.time_out?.seconds), "HH:mm:ss a");
        }else{
          endTime = moment(moment.unix(dataLogs.actual_end_time?.seconds), "HH:mm:ss a");
        }

        let min = moment.utc(moment(endTime, "HH:mm:ss").diff(moment(startTime, "HH:mm:ss"))).format("mm");
        let sec = moment.utc(moment(endTime, "HH:mm:ss").diff(moment(startTime, "HH:mm:ss"))).format("ss");
        let hour = endTime.diff(startTime, 'hours');

        let hourString = hour + "hr ";
        let minString = min + "min ";
        //let secString = sec + " and sec";

        if (hour === 0 && min !== "00") {
          duration = minString //+ secString;
        } else if (hour === 0 && min === "00") {
          duration = sec + " seconds";
        } else {
          duration = hourString + minString //+ secString;
        }

        time_duration_hour = hour;
        time_duration_minute = parseInt(min);

        if (dataLogs.amount_paid && dataLogs.amount_paid !== 0) {
          parkingFee = parseFloat(dataLogs.amount_paid);
        } else {
          parkingFee = 0;
        }

      } else {

        duration = "incomplete";

        if (dataLogs.amount_paid && dataLogs.amount_paid !== 0) {
          parkingFee = parseFloat(dataLogs.amount_paid);
        } else {
          parkingFee = 0;
        }

      }


      if (!dataLogs.amount_due) {
        dataLogs.amount_due = 0;
      }

      if (!dataLogs.zone_name) {
        if (dataLogs.station_name) {
          dataLogs.zone_name = dataLogs.station_name;
        } else {
          dataLogs.zone_name = "";
        }
      }

      if (dataLogs.processedBy == undefined) {
        dataLogs.processedBy = "";
      }else{
        dataLogs.processedBy = dataLogs.processedBy;
      }

      if (dataLogs.end_session_by == undefined) {
        dataLogs.end_session_by = "";
      }else{
        dataLogs.end_session_by = dataLogs.end_session_by;
      }


      if(dataLogs.additional_payment_recieved_by == undefined){
        dataLogs.additional_payment_recieved_by = "";
      }

      if(dataLogs.initial_payment_recieved_by == undefined){
        dataLogs.initial_payment_recieved_by = "";
      }

      if(dataLogs.paid){

        if(this.selectedReportType  == 'general'){
            dataLogs.total_payment =  dataLogs.amount_paid + dataLogs.additional_payment;
        }else{
          if(this.employeeSelected == 'all'){
            if(dataLogs.additional_payment_recieved_by_id == dataLogs.initial_payment_recieved_by_id){
              dataLogs.total_payment =  dataLogs.amount_paid + dataLogs.additional_payment;
            }else{
              dataLogs.total_payment =  dataLogs.amount_paid;
            }
          }else{
            if(dataLogs.additional_payment_recieved_by_id == this.employeeSelected){
              dataLogs.total_payment =  dataLogs.amount_paid + dataLogs.additional_payment;
            }else{
              dataLogs.total_payment =  dataLogs.amount_paid;
            }
          }
        }

      }else{
        if(dataLogs.initial_payment_recieved_by != ""){
          dataLogs.total_payment = dataLogs.amount_paid;
        }
        if(dataLogs.amount_paid == 0){
          dataLogs.total_payment = 0;
        }
      }

      if(dataLogs.total_payment == undefined){
        dataLogs.total_payment = "";
      }
      
      // Store the current log in timeInData array for later use
      this.timeInData.push(dataLogs);
      
      // Add current log to the HTML table
      arrayData.push(
        '<tr class="avoid-this-row">',
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.fullDateTimeStamp + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.zone_name + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.slot_name + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.plate_number + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.vehicle_type_custom_name + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.transaction_type_name + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.time_in + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-3 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.processedBy + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.time_out + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-3 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.end_session_by + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.duration + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + Number(dataLogs.total_payment || 0).toFixed(2) + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.amount_paid + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.initial_payment_recieved_by + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.initial_payment_recieved_date + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + (dataLogs.additional_payment || 0) + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.additional_payment_recieved_by + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + dataLogs.additional_payment_recieved_date + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + (dataLogs.discount_info || 'No Discount') + '</td>'),
        '</tr>');
    }

    return [
      '<div class="flex flex-wrap w-full overflow-x-auto mb-4 outer_canvas mt-4">' +
      '<div class="w-full lg:w-6/12">',
      '<div class="flex flex-wrap w-full">',
      '<div class="py-3 mr-2">',
      '<img src="' + this.account.photoUrl + '" alt="header_logo" class="float-left" style="width:28px;">',
      '</div>',
      '<div class="w-full lg:w-8/12 py-2">',
      '<h2 class="text-sm text-left" style="color: #000000;">' + this.account.account_name + '</strong></h2>',
      '<h2 class="text-xs text-left" style="color: #000000;">' + this.formatAddress(this.account.address) + '</strong></h2>',
      '</div>',
      '</div>',
      '</div>',
      '<div class="w-full lg:w-6/12">',
      '<div class="float-right">' +
      '<img src="/assets/img/parkbot_logo_header.png" alt="header_logo" class="float-right" style="width: 90px;">',
      '</div>',
      '</div>',
      '</div>',
      '<div class="block w-full overflow-x-auto">',
      '<h2 class="whitespace-nowrap font-semibold px-6 align-middle pt-0 pb-2 text-sm" style="background: #eeeeee;border-radius: 6px;color: #000000;overflow: inherit;">' +
      '<span class="text-left float-left">Parking Report</span>' +
      '<span class="text-right float-right"><i class="text-xs" style="vertical-align: top;position: relative;top: 2px;right: 2px;">Date covered: </i>' + startPeriodCovered + ' ~ ' + endPeriodCovered + '</span>' +
      '</h2>',
      '<div class="flex flex-wrap px-6">',
      '<div class="w-full lg:w-6/12">',
      '<h3 class="text-sm text-left py-2 float-left" style="color: #000000;">Total Transaction: <strong>' + (this.timeInData.length) + '</strong></h3>',
      '</div>',
      '<div class="w-full lg:w-6/12">',
      '<h3 class="text-sm text-left py-2 float-right" style="color: #000000;">Total Payment Collected: <strong>' + '₱' + totalFeeCollected.toFixed(2) + '</strong></h3>',
      '</div>',
      '</div>',
      '</div>',
      // Skip the filtered display section since the method doesn't exist
      '<div class="block w-full overflow-x-auto mb-12">',
      '<table class="table-block items-center w-full bg-transparent border-collapse" style="background: #eeeeee;border-radius: 6px;color: #000000;">',
      '<thead>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Date</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Zone Name</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Slot Number</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Plate Number</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Vehicle Type</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Transaction Type</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Start</th>',
      '<th class="px-3 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Started by</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">End</th>',
      '<th class="px-3 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Ended by</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Duration</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Total Payment</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Initial Rate</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Processed By</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Received initial rate</th>',  
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Additional Payment</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Processed By</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Received additional payment</th>',
      '<th class="px-2 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Discount</th>',
      
      '</thead>',
      '<tbody style="background: #ffffff;">',
      arrayData.join(''),
      '</tbody>',
      '</table>',
      '</div>'
    ].join('');
  }

  createTableForErrorLogs(start, end) {

    let arrayData = [];

    let startPeriodCovered = this.datePipe.transform(start, 'shortDate');
    let endPeriodCovered = this.datePipe.transform(end, 'shortDate');
    let currentDate = this.datePipe.transform(new Date(), 'shortDate');

    for (let i = 0; i < this.timeInData.length; i++) {

      arrayData.push(
        '<tr class="avoid-this-row">',
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2  whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + this.timeInData[i].inOrderNumberEntry + '</td>'),
        ('<td class="border-t-0  align-middle border-l-0 border-r-0 text-xs py-1 px-2  whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + this.timeInData[i].account_key + '</td>'),
        ('<td class="border-t-0  align-middle border-l-0 border-r-0 text-xs py-1 px-2  whitespace-nowrap text-left" style="width: 15px;font-size:10px;">' + this.timeInData[i].code + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap  text-left" style="width: 15px;font-size:10px;">' + this.timeInData[i].processedBy + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap  text-left" style="width: 15px;font-size:10px;">' + this.timeInData[i].status + '</td>'),
        ('<td class="border-t-0 align-middle border-l-0 border-r-0 text-xs py-1 px-2 whitespace-nowrap  text-left" style="width: 15px;font-size:10px;">' + this.timeInData[i].timestamp + '</td>'),
        '</tr>');
    }

    console.log("1:",arrayData);


    return [
      '<div class="flex flex-wrap w-full overflow-x-auto mb-4 outer_canvas mt-4">' +
      '<div class="w-full lg:w-6/12">',
      '<div class="flex flex-wrap w-full">',
      '<div class="py-3 mr-2">',
      '<img src="' + this.account.photoUrl + '" alt="header_logo" class="float-left" style="width:28px;">',
      '</div>',
      '<div class="w-full lg:w-8/12 py-2">',
      '<h2 class="text-sm text-left" style="color: #000000;">' + this.account.account_name + '</strong></h2>',
      '<h2 class="text-xs text-left" style="color: #000000;">' + this.formatAddress(this.account.address) + '</strong></h2>',
      '</div>',
      '</div>',
      '</div>',
      '<div class="w-full lg:w-6/12">',
      '<div class="float-right">' +
      '<img src="/assets/img/parkbot_logo_header.png" alt="header_logo" class="float-right" style="width: 90px;">',
      '</div>',
      '</div>',
      '</div>',
      '<div class="block w-full overflow-x-auto">',
      '<h2 class="whitespace-nowrap font-semibold px-6 align-middle pt-0 pb-2 text-sm" style="background: #eeeeee;border-radius: 6px;color: #000000;overflow: inherit;">' +
      '<span class="text-left float-left">Error Logs Report</span>' +
      '<span class="text-right float-right"><i class="text-xs" style="vertical-align: top;position: relative;top: 2px;right: 2px;">Date covered: </i>' + startPeriodCovered + ' ~ ' + endPeriodCovered + '</span>' +
      '</h2>',
      '<div class="flex flex-wrap px-6">',
      '<div class="w-full lg:w-6/12">',
      '<h3 class="text-sm text-left py-2 float-left" style="color: #000000;">Total Transaction: <strong>' + (this.timeInData.length) + '</strong></h3>',
      '</div>',
      '</div>',
      '</div>',
      '<div class="block w-full overflow-x-auto mb-12">',
      '<table class="table-block items-center w-full bg-transparent border-collapse" style="background: #eeeeee;border-radius: 6px;color: #000000;">',
      '<thead>',
      '<th class="px-0 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">#</th>',
      '<th class="px-0 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Account Key</th>',
      '<th class="px-0 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Code</th>',
      '<th class="px-0 align-middle border border-solid text-xs pt-0 py-3  border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Processed By</th>',
      '<th class="px-0 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">Status</th>',
      '<th class="px-0 align-middle border border-solid text-xs pt-0 py-3 border-l-0 border-r-0 whitespace-nowrap font-semibold text-left" style="width: 15px;font-size:10px;">DateTime</th>',
      '</thead>',
      '<tbody style="background: #ffffff;">',
      arrayData.join(''),
      '</tbody>',
      '</table>',
      '</div>'
    ].join('');
  }

  getStartOfToday() {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return firebase.firestore.Timestamp.fromDate(now);
  }

  latestDocEmails = null;

  async onSelectMonth(value:any){
    // Use the value directly since it's passed from the template
    this.selectedMonth = value;
  }

  async onSelectYear(value:any){
    // Use the value directly since it's passed from the template
    this.selectedYear = value;
  }



  generateWeeklyDateRanges() {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    //const currentMonth = currentDate.getMonth();
    //always start the week in 1st week of january
    const currentMonth = 0;
    const startDate = new Date(currentYear, currentMonth, 1);
    const endDate = new Date(currentYear, currentMonth + 1, 0);

    while (startDate <= endDate) {
      const weekStartDate = new Date(startDate);
      const weekEndDate = new Date(startDate);
      weekEndDate.setDate(weekEndDate.getDate() + 6);

      this.weeklyDateRanges.push({
        start: this.formatDate(weekStartDate),
        end: this.formatDate(weekEndDate)
      });

      startDate.setDate(startDate.getDate() + 7);
    }
  }

  formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  getLastDateOfMonth(year, month) {
    // Create a new Date object for the first day of the next month
    const nextMonth = new Date(year, month, 1);

    // Subtract one day to get the last day of the current month
    nextMonth.setDate(nextMonth.getDate() - 1);

    return nextMonth.getDate();
  }

  /**
   * Get value from a summary row for a specific header and property
   */
  getSummaryValue(row: any, header: string, property: string): number {
    if (!row || !row.headers || !row.headers.values) return 0;
    
    const headerValue = row.headers.values.find((v: any) => v.name === header);
    return headerValue ? headerValue[property] || 0 : 0;
  }
  
  /**
   * Calculate total for all values in a row
   */
  getTotalForRow(row: any, property: string): number {
    if (!row || !row.headers || !row.headers.values) return 0;
    
    return row.headers.values.reduce((total: number, value: any) => {
      return total + (value[property] || 0);
    }, 0);
  }
  
  /**
   * Calculate total for a specific column across all rows
   */
  getColumnTotal(header: string, property: string): number {
    if (!this.tableData) return 0;
    
    return this.tableData.reduce((total: number, row: any) => {
      return total + this.getSummaryValue(row, header, property);
    }, 0);
  }
  
  /**
   * Get occupancy rate for a specific date
   */
  getOccupancyRate(row: any, date: string): number {
    if (!row || !row.dates) return 0;
    
    const dateData = row.dates.find((d: any) => d.date === date);
    return dateData ? dateData.occupancyRate || 0 : 0;
  }
  
  /**
   * Calculate average occupancy for a row
   */
  getAverageOccupancy(row: any): number {
    if (!row || !row.dates || row.dates.length === 0) return 0;
    
    const total = row.dates.reduce((sum: number, date: any) => {
      return sum + (date.occupancyRate || 0);
    }, 0);
    
    return total / row.dates.length;
  }
  
  /**
   * Update the table headers and structure based on report type
   */
  updateTableForReportType() {
    switch (this.selectedReportType) {
      case 'general':
        this.tableHeaders = [
          'Date',
          'Plate Number',
          'Zone Name',
          'Slot',
          'Vehicle Type',
          'Transaction Type',
          'Start',
          'Started By',
          'End',
          'Ended By',
          'Status',
          'Duration',
          'Initial Rate',
          'Initial Rate Received By',
          'Initial Rate Received Date',
          'Additional Payment',
          'Additional Payment Received By',
          'Additional Payment Received Date',
          'Refund Amount',
          'Refund Info',
          'Cancel Amount',
          'Cancel Info',
          'Total Payment',
          'Discount Savings',
          'Discounted Amount',
          'Discount Details',
          'Net Amount'
        ];
        
        // Initialize column visibility
        this.allColumns = [...this.tableHeaders];
        
        // Try to load saved preferences, otherwise use defaults
        const preferencesLoaded = this.loadColumnPreferences();
        if (!preferencesLoaded) {
          this.visibleColumns.clear();
          this.defaultVisibleColumns.forEach(col => {
            if (this.allColumns.includes(col)) {
              this.visibleColumns.add(col);
            }
          });
        }
        
        // Set default sorting to Start (descending) for general report
        this.sortColumn = 'time_in_display';
        this.sortDirection = 'desc';
        break;
        
      case 'dsa': // Summary per Attendant
        // Get unique attendant names for headers
        this.summaryHeaders = [];
        this.tableData.forEach(row => {
          if (row.headers && row.headers.values) {
            row.headers.values.forEach(value => {
              if (!this.summaryHeaders.includes(value.name)) {
                this.summaryHeaders.push(value.name);
              }
            });
          }
        });
        break;
        
      case 'dstt': // Summary per Transaction Type
        // Get unique transaction types for headers
        this.summaryHeaders = [];
        this.tableData.forEach(row => {
          if (row.headers && row.headers.values) {
            row.headers.values.forEach(value => {
              if (!this.summaryHeaders.includes(value.name)) {
                this.summaryHeaders.push(value.name);
              }
            });
          }
        });
        break;
        
      case 'vehicle': // Summary per Vehicle Type
        // Get unique vehicle types for headers
        this.summaryHeaders = [];
        this.tableData.forEach(row => {
          if (row.headers && row.headers.values) {
            row.headers.values.forEach(value => {
              if (!this.summaryHeaders.includes(value.name)) {
                this.summaryHeaders.push(value.name);
              }
            });
          }
        });
        break;
        
      case 'apdtt': // Average Parking Duration per Transaction Type
        this.tableHeaders = [
          'Transaction Type', 
          'Vehicle Count', 
          'Average Duration (Minutes)', 
          'Average Duration (Hours)'
        ];
        break;
        
      case 'apo': // Average Parking Occupancy
        // Get unique dates for occupancy report
        this.occupancyDates = [];
        if (this.tableData && this.tableData.length > 0 && this.tableData[0].dates) {
          this.occupancyDates = this.tableData[0].dates.map((d: any) => d.date);
        }
        this.tableHeaders = ['Time Range', ...this.occupancyDates, 'Average'];
        break;

      case 'unpaid': // Unpaid Transactions Summary
        this.tableHeaders = ['Zone', 'Transaction Count', 'Total Amount Due'];
        break;
        
      case 'voided': // Voided/Refunded Transactions
        this.tableHeaders = ['Date', 'Plate Number', 'Zone', 'Amount', 'Reason', 'Processed By', 'Date'];
        break;
        
      case 'status': // Transaction Status Breakdown
        this.tableHeaders = ['Status', 'Count', 'Percentage'];
        break;
        
      case 'payment': // Revenue by Payment Type
        this.tableHeaders = ['Payment Type', 'Transaction Count', 'Total Amount', 'Percentage of Revenue'];
        break;
        
      case 'discount': // Discount Utilization Report
        this.tableHeaders = ['Date', 'Plate Number', 'Discount Type', 'Discount Percent', 'Discounted Amount', 'Processed By'];
        break;
        
      case 'duration': // Parking Duration Distribution
        this.tableHeaders = ['Duration Range', 'Count', 'Percentage'];
        break;
        
      case 'employee': // Employee Performance Report
        this.tableHeaders = ['Employee', 'Transactions Started', 'Transactions Completed', 'Total Revenue', 'Avg Transaction Value'];
        break;
        
      case 'trend': // Transaction Trend (Per Day)
        this.tableHeaders = ['Date', 'Transaction Count', 'Total Amount'];
        break;
        
      case 'assignment': // Manual vs Automatic Assignment
        this.tableHeaders = ['Assignment Type', 'Count', 'Percentage'];
        break;
        
      case 'vehicle-distribution': // Vehicle Type Distribution
        this.tableHeaders = ['Vehicle Type', 'Count', 'Percentage', 'Revenue', 'Avg Revenue/Vehicle'];
        break;
        
      default:
        // Default to some common columns
        this.tableHeaders = ['Date', 'Description', 'Value'];
    }
  }

  /**
   * Export to Excel using XLSX library
   */
  async exportToExcel() {
    // First make sure we have data
    if (!this.tableData || this.tableData.length === 0) {
      Swal.fire({
        title: 'No Data',
        text: 'There is no data to export. Please generate a report first.',
        icon: 'warning'
      });
      return;
    }

    try {
      // Log export action
      await this.auditService.logExport(
        'excel_report',
        `report_${new Date().toISOString().split('T')[0]}.xlsx`,
        this.tableData.length
      );
      // Prepare data based on report type
      const exportData = this.prepareExportData();
      
      // Convert to worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
      
      // Generate file name with date
      const fileName = `${this.selectedReportType}_report_${new Date().toISOString().slice(0,10)}.xlsx`;
      
      // Save file
      XLSX.writeFile(workbook, fileName);
      
      // Show success message
      Swal.fire({
        title: 'Success',
        text: 'Excel file has been downloaded successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to export to Excel. Please try again.',
        icon: 'error'
      });
    }
  }

  /**
   * Export to Word document using docx library
   */
  async exportToWord() {
    // First make sure we have data
    if (!this.tableData || this.tableData.length === 0) {
      Swal.fire({
        title: 'No Data',
        text: 'There is no data to export. Please generate a report first.',
        icon: 'warning'
      });
      return;
    }

    // Log export action
    await this.auditService.logExport(
      'word_report',
      `report_${new Date().toISOString().split('T')[0]}.docx`,
      this.tableData.length
    );

    try {
      // Show loading indicator
      Swal.fire({
        title: 'Generating Word Document...',
        text: 'Please wait while we prepare your document.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Prepare data based on report type
      const exportData = this.prepareExportData();
      
      // Create document
      const doc = this.generateWordDocument(exportData);
      
      // Generate file name
      const fileName = `${this.selectedReportType}_report_${new Date().toISOString().slice(0,10)}.docx`;
      
      // Generate blob and save file
      docx.Packer.toBlob(doc).then(blob => {
        saveAs(blob, fileName);
        
        Swal.fire({
          title: 'Success',
          text: 'Word document has been downloaded successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      });
    } catch (error) {
      console.error('Error exporting to Word:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to export to Word. Please try again.',
        icon: 'error'
      });
    }
  }

  /**
   * Generate Word document structure
   */
  private generateWordDocument(data: any[]): docx.Document {
    const { Document, Paragraph, Table, TableRow, TableCell, TextRun, BorderStyle, WidthType, HeadingLevel } = docx;

    // Get headers
    const headers = Object.keys(data[0] || {});

    // Create title
    let title = 'General Report';
    switch (this.selectedReportType) {
      case 'general': title = 'General Report'; break;
      case 'unpaid': title = 'Unpaid Transactions Summary'; break;
      case 'voided': title = 'Voided/Refunded Transactions'; break;
      case 'status': title = 'Transaction Status Breakdown'; break;
      case 'payment': title = 'Revenue by Payment Type'; break;
      case 'discount': title = 'Discount Utilization Report'; break;
      case 'duration': title = 'Parking Duration Distribution'; break;
      case 'employee': title = 'Employee Performance Report'; break;
      case 'trend': title = 'Transaction Trend (Per Day)'; break;
      case 'assignment': title = 'Manual vs Automatic Assignment'; break;
      default: title = `${this.selectedReportType.charAt(0).toUpperCase() + this.selectedReportType.slice(1)} Report`;
    }

    // Create header row
    const headerRow = new TableRow({
      tableHeader: true,
      children: headers.map(header => 
        new TableCell({
          shading: { fill: "EEEEEE" },
          children: [new Paragraph({ 
            text: header.charAt(0).toUpperCase() + header.slice(1).replace(/_/g, ' '),
            heading: HeadingLevel.HEADING_5
          })],
        })
      ),
    });

    // Create data rows
    const rows = data.map(item => 
      new TableRow({
        children: headers.map(header => 
          new TableCell({
            children: [new Paragraph(item[header]?.toString() || '')],
          })
        ),
      })
    );

    // Create table
    const table = new Table({
      rows: [headerRow, ...rows],
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
    });

    // Create document
    return new Document({
      sections: [
        {
          properties: {
            page: {
              size: {
                orientation: 'landscape'
              },
              margin: {
                top: 1440,
                right: 1440,
                bottom: 1440,
                left: 1440
              }
            }
          },
          children: [
            new Paragraph({
              text: title,
              heading: HeadingLevel.HEADING_1,
            }),
            new Paragraph({
              text: `Report Period: ${this.reportPeriod}`,
              heading: HeadingLevel.HEADING_3,
            }),
            new Paragraph({
              text: `Generated On: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
            }),
            new Paragraph({
              text: ' '
            }),
            new Paragraph({
              text: `Total Transactions: ${this.totalTransactions}`,
            }),
            new Paragraph({
              text: `Total Amount: ₱${this.totalAmount.toFixed(2)}`,
            }),
            ...(this.totalCanceledCount > 0 ? [
              new Paragraph({
                text: `Cancelled Transactions: ${this.totalCanceledCount}`,
              })
            ] : []),
            ...(this.totalUnpaidCount > 0 ? [
              new Paragraph({
                text: `Unpaid Transactions: ₱${this.totalUnpaidAmount.toFixed(2)} (${this.totalUnpaidCount})`,
              })
            ] : []),
            ...(this.totalRefundedAmount > 0 ? [
              new Paragraph({
                text: `Total Refunded Amount: ₱${this.totalRefundedAmount.toFixed(2)}`,
              }),
              new Paragraph({
                text: `Net Amount: ₱${this.netAmount.toFixed(2)}`,
              })
            ] : []),
            new Paragraph({
              text: ' '
            }),
            table,
          ],
        },
      ],
    });
  }

  /**
   * Helper method to prepare export data based on report type
   */
  private prepareExportData(): any[] {
    let exportData: any[] = [];

    switch (this.selectedReportType) {
      case 'general':
        // For general report, match CSV format exactly
        exportData = this.getFilteredData().map(row => {
          return {
            'Date': row.date_display || '',
            'Plate Number': row.plate_number || '',
            'Zone Name': row.zone_name || '',
            'Slot': row.slot_name || '',
            'Vehicle Type': row.vehicle_type_custom_name || '',
            'Transaction Type': row.transaction_type_name || '',
            'Start': row.time_in_display || '',
            'Started By': row.processedBy || '',
            'End': row.time_out_display || '',
            'Ended By': row.end_session_by || '',
            'Duration': row.duration || '',
            'Initial Rate': row.initial_payment ? row.initial_payment.toFixed(2) : '0.00',
            'Initial Rate Received By': row.initial_payment_recieved_by || '',
            'Initial Rate Received Date': row.initial_payment_received_date_display || '',
            'Additional Payment': row.additional_payment ? row.additional_payment.toFixed(2) : '0.00',
            'Additional Payment Received By': row.additional_payment_recieved_by || '',
            'Additional Payment Received Date': row.additional_payment_received_date_display || '',
            'Refund Amount': row.refund_amount ? row.refund_amount.toFixed(2) : '0.00',
            'Refund Info': row.refund_info || '',
            'Cancel Amount': row.cancel_amount ? row.cancel_amount.toFixed(2) : '0.00',
            'Cancel Info': row.cancel_info || '',
            'Total Payment': row.total_payment ? row.total_payment.toFixed(2) : '0.00',
            'Discount Savings': row.discount_savings ? row.discount_savings.toFixed(2) : '0.00',
            'Discounted Amount': row.discounted_amount ? row.discounted_amount.toFixed(2) : '0.00',
            'Discount Details': row.discount_details || '',
            'Net Amount': row.net_amount ? row.net_amount.toFixed(2) : '0.00'
          };
        });

        // Add summary data to match CSV format
        exportData.push({});
        exportData.push({'Date': 'Total Transactions', 'Plate Number': this.totalTransactions});
        exportData.push({'Date': 'Total Amount', 'Plate Number': this.totalAmount.toFixed(2)});

        // Add cancelled count and amount if available
        if (this.totalCanceledCount > 0) {
          exportData.push({'Date': 'Cancelled Transactions', 'Plate Number': `${this.totalCanceledCount} (₱${this.totalCancelledAmount.toFixed(2)})`});
        }

        // Add unpaid amount and count if available
        if (this.totalUnpaidCount > 0) {
          exportData.push({'Date': 'Unpaid Transactions', 'Plate Number': `${this.totalUnpaidAmount.toFixed(2)} (${this.totalUnpaidCount})`});
        }

        exportData.push({'Date': 'Total Refunded Amount', 'Plate Number': this.totalRefundedAmount ? this.totalRefundedAmount.toFixed(2) : '0.00'});
        exportData.push({'Date': 'Net Amount (Collected)', 'Plate Number': this.totalNetAmount ? this.totalNetAmount.toFixed(2) : '0.00'});
        break;
        
      case 'dsa': // Special handling for Summary per Attendant
        // Create a more detailed export format for DSA reports
        exportData = [];
        
        // Add header row with company info
        exportData.push({
          company: this.selectedZone?.zone_name || 'All Zones',
          employee: '',
          period: this.reportPeriod,
          report_type: 'Daily Summary per Attendant'
        });
        
        // Add transaction count section
        exportData.push({
          company: `Total Transaction: ${this.totalTransactions}`,
          employee: '',
          period: '',
          report_type: ''
        });
        
        // Add column headers
        const headers = ['Date', ...this.summaryHeaders, 'Grand Total'];
        const headerRow = {};
        headers.forEach((header, index) => {
          headerRow[`col${index}`] = header;
        });
        exportData.push(headerRow);
        
        // Add transaction count data rows
        this.getFilteredData().forEach(row => {
          const dataRow = {
            col0: row.date
          };
          
          this.summaryHeaders.forEach((header, index) => {
            dataRow[`col${index + 1}`] = this.getSummaryValue(row, header, 'count');
          });
          
          dataRow[`col${this.summaryHeaders.length + 1}`] = this.getTotalForRow(row, 'count');
          exportData.push(dataRow);
        });
        
        // Add totals row
        const totalsRow = {
          col0: 'GRAND TOTAL'
        };
        
        this.summaryHeaders.forEach((header, index) => {
          totalsRow[`col${index + 1}`] = this.getColumnTotal(header, 'count');
        });
        
        totalsRow[`col${this.summaryHeaders.length + 1}`] = this.totalTransactions;
        exportData.push(totalsRow);
        
        // Add empty row as separator
        exportData.push({});
        
        // Add sales and other summary info
        exportData.push({
          company: `Total Sales: ${this.totalAmount.toFixed(2)}`,
          employee: '',
          period: '',
          report_type: ''
        });
        
        // Add cancelled count if available
        if (this.totalCanceledCount > 0) {
          exportData.push({
            company: `Cancelled Transactions: ${this.totalCanceledCount}`,
            employee: '',
            period: '',
            report_type: ''
          });
        }

        // Add unpaid amount and count if available
        if (this.totalUnpaidCount > 0) {
          exportData.push({
            company: `Unpaid Transactions: ${this.totalUnpaidAmount.toFixed(2)} (${this.totalUnpaidCount})`,
            employee: '',
            period: '',
            report_type: ''
          });
        }
        
        // Add refund information if available
        if (this.totalRefundedAmount > 0) {
          exportData.push({
            company: `Total Refunded Amount: ${this.totalRefundedAmount.toFixed(2)}`,
            employee: '',
            period: '',
            report_type: ''
          });
          exportData.push({
            company: `Net Amount: ${this.netAmount.toFixed(2)}`,
            employee: '',
            period: '',
            report_type: ''
          });
        }
        
        // Add column headers again for sales
        exportData.push(headerRow);
        
        // Add sales data rows
        this.getFilteredData().forEach(row => {
          const dataRow = {
            col0: row.date
          };
          
          this.summaryHeaders.forEach((header, index) => {
            dataRow[`col${index + 1}`] = this.getSummaryValue(row, header, 'sales');
          });
          
          dataRow[`col${this.summaryHeaders.length + 1}`] = this.getTotalForRow(row, 'sales');
          exportData.push(dataRow);
        });
        
        // Add sales totals row
        const salesTotalsRow = {
          col0: 'GRAND TOTAL'
        };
        
        this.summaryHeaders.forEach((header, index) => {
          salesTotalsRow[`col${index + 1}`] = this.getColumnTotal(header, 'sales');
        });
        
        salesTotalsRow[`col${this.summaryHeaders.length + 1}`] = this.totalAmount;
        exportData.push(salesTotalsRow);
        break;

      case 'unpaid':
      case 'voided':
      case 'status':
      case 'payment':
      case 'discount':
      case 'duration':
      case 'employee':
      case 'trend':
      case 'assignment':
      case 'vehicle':
        // For other reports, use the filtered data
        exportData = this.getFilteredData();
        break;

      default:
        exportData = this.getFilteredData();
    }

    return exportData;
  }

  /**
   * Helper method to check if a log meets employee filter criteria
   */
  meetsEmployeeFilter(dataLog: any): boolean {
    if (this.employeeSelected === 'all') {
      return true;
    }

    return dataLog.additional_payment_recieved_by_id === this.employeeSelected ||
           dataLog.initial_payment_recieved_by_id === this.employeeSelected ||
           dataLog.paidBy_id === this.employeeSelected ||
           dataLog.processedBy_id === this.employeeSelected;
  }

  /**
   * Helper method to group data by zone
   */
  groupByZone(data: any[]): any[] {
    const groupedData = {};

    data.forEach(item => {
      const zoneName = item.zone_name || 'Unknown Zone';

      if (!groupedData[zoneName]) {
        groupedData[zoneName] = {
          zone_name: zoneName,
          transaction_count: 0,
          total_amount: 0
        };
      }

      groupedData[zoneName].transaction_count++;
      groupedData[zoneName].total_amount += parseFloat(item.total_payment || item.amount_due || 0);
    });

    return Object.values(groupedData);
  }

  /**
   * Helper method to calculate summary metrics for all report types
   */
  calculateSummaryMetrics(logs: any[]): any {
    let totalAmount = 0;
    let totalRefundedAmount = 0;
    let totalCanceledCount = 0;
    let totalCancelledAmount = 0;
    let totalUnpaidAmount = 0;
    let totalUnpaidCount = 0;
    let totalNetAmount = 0;
    let totalTransactions = 0;

    for (const log of logs) {
      const dataLog = log.data ? log.data() : log;

      // Filter by employee if needed
      if (!this.meetsEmployeeFilter(dataLog)) {
        continue;
      }

      totalTransactions++;

      // Check if it's a canceled transaction
      if (dataLog.isCanceled || dataLog.status === 'cancelled') {
        totalCanceledCount++;
        // Track the cancelled amount (amount that was paid but is now cancelled)
        if (dataLog.paid && dataLog.amount_paid !== undefined && dataLog.amount_paid !== null) {
          totalCancelledAmount += parseFloat(dataLog.amount_paid);
        }
      }

      // Check if it's an unpaid transaction and add to unpaid amount and count
      if (!dataLog.paid && dataLog.status !== 'cancelled' && !dataLog.isCanceled) {
        totalUnpaidCount++;
        // Add the total payment amount for unpaid transactions
        if (dataLog.total_payment) {
          totalUnpaidAmount += parseFloat(dataLog.total_payment);
        }
      }

      // Calculate total amount
      if (dataLog.total_payment) {
        totalAmount += parseFloat(dataLog.total_payment);
      }

      // Calculate refunded amount
      if (dataLog.refund_amount) {
        totalRefundedAmount += parseFloat(dataLog.refund_amount);
      }

      // Calculate total net amount based on actual amount paid
      // Deduct both refunds and cancellations
      if (dataLog.paid && dataLog.status !== 'cancelled' && !dataLog.isCanceled) {
        if (dataLog.amount_paid !== undefined && dataLog.amount_paid !== null) {
          let netFromTransaction = parseFloat(dataLog.amount_paid);
          // Deduct refund if applicable
          if (dataLog.isRefunded && dataLog.refund_amount) {
            netFromTransaction -= parseFloat(dataLog.refund_amount);
          }
          totalNetAmount += netFromTransaction;
        } else {
          // Fallback calculation
          const total = parseFloat(dataLog.total_payment || 0);
          const refund = parseFloat(dataLog.refund_amount || 0);
          totalNetAmount += (total - refund);
        }
      }
    }

    return {
      totalAmount,
      totalRefundedAmount,
      totalCanceledCount,
      totalCancelledAmount,
      totalUnpaidAmount,
      totalUnpaidCount,
      netAmount: totalAmount - totalRefundedAmount - totalCancelledAmount,
      totalNetAmount,
      totalTransactions
    };
  }

  /**
   * Process logs for unpaid transactions summary
   */
  async processUnpaidTransactionsReport(logs: any) {
    let unpaidData = [];
    let totalUnpaidAmount = 0;

    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    for (const log of logs.docs) {
      const dataLog = log.data();

      // Filter for unpaid transactions
      if (!dataLog.paid && dataLog.status !== 'cancelled' && !dataLog.isCanceled &&
          this.meetsEmployeeFilter(dataLog)) {

        try {
          // Process log for display
          let processedLog = this.processLogForDisplay(dataLog);

          // Calculate amount due if available
          const amountDue = parseFloat(dataLog.total_payment || dataLog.amount_due || 0);
          totalUnpaidAmount += amountDue;

          // Add to unpaid data
          unpaidData.push({
            ...processedLog,
            amount_due: amountDue
          });
        } catch (error) {
          console.error('Error processing unpaid log:', error, dataLog);
        }
      }
    }

    // Group by zone if needed
    const groupedByZone = this.groupByZone(unpaidData);

    return {
      data: groupedByZone,
      totalTransactions: unpaidData.length,
      totalAmount: totalUnpaidAmount,
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }

  /**
   * Process logs for voided/refunded transactions
   */
  async processVoidedRefundedReport(logs: any) {
    let voidedData = [];
    let totalAmount = 0;

    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    for (const log of logs.docs) {
      const dataLog = log.data();

      // Filter for voided or refunded transactions
      if ((dataLog.isVoided === true || dataLog.isRefunded === true || dataLog.isCanceled === true || dataLog.status === 'cancelled') &&
          this.meetsEmployeeFilter(dataLog)) {

        try {
          // Process log for display
          let processedLog = this.processLogForDisplay(dataLog);

          // Get refund amount and reason
          let amount;
          if (dataLog.isRefunded && dataLog.refund_amount) {
            amount = parseFloat(dataLog.refund_amount || 0);
          } else {
            amount = parseFloat(dataLog.total_payment || dataLog.amount_paid || 0);
          }
          totalAmount += amount;

          // Add voided/refunded info
          voidedData.push({
            ...processedLog,
            void_refund_amount: amount,
            void_refund_reason: dataLog.void_reason || dataLog.refund_reason || dataLog.cancellation_reason || 'Not specified',
            void_refund_by: dataLog.void_by || dataLog.refunded_by || dataLog.cancelled_by || 'Unknown',
            void_refund_date: processedLog.refunded_at_formatted || processedLog.cancelled_at_formatted || 'Unknown'
          });
        } catch (error) {
          console.error('Error processing voided/refunded log:', error, dataLog);
        }
      }
    }

    return {
      data: voidedData,
      totalTransactions: voidedData.length,
      totalAmount: totalAmount,
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }

  /**
   * Process logs for transaction status breakdown
   */
  async processStatusBreakdownReport(logs: any) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    // Define status categories
    const statusCategories = {
      'active': 0,
      'completed': 0,
      'cancelled': 0,
      'refunded': 0,
      'voided': 0
    };

    const statusData = [];

    for (const log of logs.docs) {
      const dataLog = log.data();

      if (this.meetsEmployeeFilter(dataLog)) {
        // Determine status
        let status = dataLog.session_status_text || 'unknown';
        status = status.toLowerCase();

        // Update counts
        if (statusCategories.hasOwnProperty(status)) {
          statusCategories[status]++;
        } else if (dataLog.isVoided) {
          statusCategories['voided']++;
        } else if (dataLog.isRefunded) {
          statusCategories['refunded']++;
        } else if (dataLog.time_out) {
          statusCategories['completed']++;
        } else {
          statusCategories['active']++;
        }
      }
    }

    // Convert to array format
    for (const [status, count] of Object.entries(statusCategories)) {
      statusData.push({
        status: status.charAt(0).toUpperCase() + status.slice(1),
        count: count,
        percentage: (count / summaryMetrics.totalTransactions * 100).toFixed(2) + '%'
      });
    }

    return {
      data: statusData,
      totalTransactions: summaryMetrics.totalTransactions,
      totalAmount: summaryMetrics.totalAmount,
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }

  /**
   * Process logs for revenue by payment type
   */
  async processRevenueByPaymentReport(logs: any) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    // Define payment type categories
    const paymentTypes = {};
    const revenueData = [];
    let totalAmount = 0;

    for (const log of logs.docs) {
      const dataLog = log.data();

      if (dataLog.paid && this.meetsEmployeeFilter(dataLog)) {
        // Get payment type
        const paymentType = dataLog.payment_type || 'regular';

        // Get amount
        const amount = parseFloat(dataLog.total_payment || dataLog.amount_paid || 0);
        totalAmount += amount;

        // Update payment type data
        if (!paymentTypes[paymentType]) {
          paymentTypes[paymentType] = {
            payment_type: paymentType,
            transaction_count: 0,
            total_amount: 0
          };
        }

        paymentTypes[paymentType].transaction_count++;
        paymentTypes[paymentType].total_amount += amount;
      }
    }
    
    // Convert to array and calculate percentages
    for (const type of Object.values(paymentTypes)) {
      const typeObj = type as any;
      revenueData.push({
        payment_type: typeObj.payment_type,
        transaction_count: typeObj.transaction_count,
        total_amount: typeObj.total_amount,
        percentage_of_revenue: ((typeObj.total_amount / totalAmount) * 100).toFixed(2) + '%'
      });
    }
    
    return {
      data: revenueData,
      totalTransactions: summaryMetrics.totalTransactions,
      totalAmount: summaryMetrics.totalAmount,
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }

  /**
   * Process logs for discount utilization
   */
  async processDiscountReport(logs: any) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    let discountData = [];
    let totalDiscount = 0;

    for (const log of logs.docs) {
      const dataLog = log.data();

      // Filter for transactions with discounts
      if (dataLog.discount_applied === true && this.meetsEmployeeFilter(dataLog)) {
        try {
          // Process log for display
          let processedLog = this.processLogForDisplay(dataLog);

          // Calculate discount amount
          const discountAmount = parseFloat(dataLog.discounted_amount || 0);
          totalDiscount += discountAmount;

          // Add discount info
          discountData.push({
            ...processedLog,
            discount_type: dataLog.discount_info || 'Unknown',
            discount_percent: dataLog.discount_percent ? dataLog.discount_percent + '%' : 'Unknown',
            discounted_amount: discountAmount
          });
        } catch (error) {
          console.error('Error processing discount log:', error, dataLog);
        }
      }
    }

    return {
      data: discountData,
      totalTransactions: discountData.length,
      totalAmount: totalDiscount,
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }

  /**
   * Process logs for parking duration distribution
   */
  async processDurationDistributionReport(logs: any) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    // Define duration buckets (in minutes)
    const durationBuckets = {
      '0-30 min': 0,
      '31-60 min': 0,
      '1-2 hours': 0,
      '2-3 hours': 0,
      '3-4 hours': 0,
      '4-8 hours': 0,
      '8+ hours': 0
    };

    let totalTransactions = 0;
    
    for (const log of logs.docs) {
      const dataLog = log.data();
      
      // Only include completed sessions with time_in and time_out
      if (dataLog.time_in && dataLog.time_out && this.meetsEmployeeFilter(dataLog)) {
        totalTransactions++;
        
        // Calculate duration in minutes
        const timeIn = dataLog.time_in.seconds * 1000;
        const timeOut = dataLog.time_out.seconds * 1000;
        const durationMinutes = (timeOut - timeIn) / (1000 * 60);
        
        // Determine which bucket this falls into
        if (durationMinutes <= 30) {
          durationBuckets['0-30 min']++;
        } else if (durationMinutes <= 60) {
          durationBuckets['31-60 min']++;
        } else if (durationMinutes <= 120) {
          durationBuckets['1-2 hours']++;
        } else if (durationMinutes <= 180) {
          durationBuckets['2-3 hours']++;
        } else if (durationMinutes <= 240) {
          durationBuckets['3-4 hours']++;
        } else if (durationMinutes <= 480) {
          durationBuckets['4-8 hours']++;
        } else {
          durationBuckets['8+ hours']++;
        }
      }
    }
    
    // Convert to array format with percentages
    const durationData = Object.entries(durationBuckets).map(([range, count]) => {
      return {
        duration_range: range,
        count: count,
        percentage: totalTransactions > 0 ? ((count / totalTransactions) * 100).toFixed(2) + '%' : '0%'
      };
    });
    
    return {
      data: durationData,
      totalTransactions: totalTransactions,
      totalAmount: summaryMetrics.totalAmount,
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }

  /**
   * Process logs for employee performance
   */
  async processEmployeePerformanceReport(logs: any) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    const employeePerformance = {};
    let totalRevenue = 0;
    
    for (const log of logs.docs) {
      const dataLog = log.data();
      
      // Get employee ID and name
      const employeeId = dataLog.processedBy_id || dataLog.initial_payment_recieved_by_id || 'unknown';
      const employeeName = dataLog.processedBy || dataLog.initial_payment_recieved_by || 'Unknown';
      
      if (employeeId !== 'unknown' && (this.employeeSelected === 'all' || this.employeeSelected === employeeId)) {
        // Get amount
        const amount = parseFloat(dataLog.total_payment || dataLog.amount_paid || 0);
        totalRevenue += amount;
        
        // Initialize employee data if not exists
        if (!employeePerformance[employeeId]) {
          employeePerformance[employeeId] = {
            employee_id: employeeId,
            employee_name: employeeName,
            transactions_started: 0,
            transactions_completed: 0,
            total_revenue: 0,
            average_transaction_value: 0
          };
        }
        
        // Update employee data
        if (dataLog.processedBy_id === employeeId) {
          employeePerformance[employeeId].transactions_started++;
        }
        
        if (dataLog.time_out && dataLog.end_session_by_id === employeeId) {
          employeePerformance[employeeId].transactions_completed++;
        }
        
        if (dataLog.paid && 
            (dataLog.initial_payment_recieved_by_id === employeeId || 
             dataLog.additional_payment_recieved_by_id === employeeId)) {
          employeePerformance[employeeId].total_revenue += amount;
        }
      }
    }
    
    // Calculate averages and convert to array
    const performanceData = Object.values(employeePerformance).map(employee => {
      const employeeObj = employee as any;
      return {
        employee_id: employeeObj.employee_id,
        employee_name: employeeObj.employee_name,
        transactions_started: employeeObj.transactions_started,
        transactions_completed: employeeObj.transactions_completed,
        total_revenue: employeeObj.total_revenue,
        average_transaction_value: employeeObj.transactions_started > 0 ? 
          (employeeObj.total_revenue / employeeObj.transactions_started).toFixed(2) : '0.00'
      };
    });
    
    return {
      data: performanceData,
      totalTransactions: summaryMetrics.totalTransactions,
      totalAmount: summaryMetrics.totalAmount,
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }

  /**
   * Process logs for transaction trend per day
   */
  async processTransactionTrendReport(logs: any, start: Date, end: Date) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    // Create object to hold counts per day
    const trendData = {};
    let currentDate = new Date(start);
    
    // Initialize all days in the range with zero
    while (currentDate <= end) {
      const dateString = currentDate.toISOString().split('T')[0];
      trendData[dateString] = {
        date: dateString,
        transaction_count: 0,
        total_amount: 0
      };
      
      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Count transactions by day
    for (const log of logs.docs) {
      const dataLog = log.data();
      
      if (this.meetsEmployeeFilter(dataLog)) {
        // Get transaction date
        const timeIn = new Date(dataLog.time_in.seconds * 1000);
        const dateString = timeIn.toISOString().split('T')[0];
        
        // Update count if date exists in our range
        if (trendData[dateString]) {
          trendData[dateString].transaction_count++;
          trendData[dateString].total_amount += parseFloat(dataLog.total_payment || dataLog.amount_paid || 0);
        }
      }
    }
    
    // Convert to array and sort by date
    const result = Object.values(trendData).sort((a: any, b: any) => 
      a.date.localeCompare(b.date)
    );
    
    return {
      data: result,
      totalTransactions: summaryMetrics.totalTransactions,
      totalAmount: summaryMetrics.totalAmount,
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }

  /**
   * Process logs for manual vs automatic assignment
   */
  async processAssignmentTypeReport(logs: any) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    let manualCount = 0;
    let autoCount = 0;
    let totalCount = 0;
    
    for (const log of logs.docs) {
      const dataLog = log.data();
      
      if (this.meetsEmployeeFilter(dataLog)) {
        totalCount++;
        
        if (dataLog.is_manual_assignment === true) {
          manualCount++;
        } else {
          autoCount++;
        }
      }
    }
    
    const assignmentData = [
      {
        assignment_type: 'Manual Assignment',
        count: manualCount,
        percentage: totalCount > 0 ? ((manualCount / totalCount) * 100).toFixed(2) + '%' : '0%'
      },
      {
        assignment_type: 'Automatic Assignment',
        count: autoCount,
        percentage: totalCount > 0 ? ((autoCount / totalCount) * 100).toFixed(2) + '%' : '0%'
      }
    ];
    
    return {
      data: assignmentData,
      totalTransactions: totalCount,
      totalAmount: summaryMetrics.totalAmount,
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }


  /**
   * Process logs for vehicle type distribution
   */
  async processVehicleTypeDistributionReport(logs: any) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    // Initialize vehicle type tracking
    const vehicleTypes = {};
    const vehicleRevenue = {};

    let totalTransactions = 0;
    let totalAmount = 0;
    
    // First pass - collect all unique vehicle types that exist in the data
    for (const log of logs.docs) {
      const dataLog = log.data();
      
      if (this.meetsEmployeeFilter(dataLog)) {
        // Get the vehicle type from the data
        const vehicleType = dataLog.vehicle_type_custom_name || 'Unknown';
        
        // Add to our tracking if not already present
        if (!vehicleTypes.hasOwnProperty(vehicleType)) {
          vehicleTypes[vehicleType] = 0;
          vehicleRevenue[vehicleType] = 0;
        }
      }
    }
    
    // If no vehicle types found, add some default ones
    if (Object.keys(vehicleTypes).length === 0) {
      vehicleTypes['Light Vehicle'] = 0;
      vehicleTypes['Motorcycle'] = 0;
      vehicleTypes['Other'] = 0;
      vehicleRevenue['Light Vehicle'] = 0;
      vehicleRevenue['Motorcycle'] = 0;
      vehicleRevenue['Other'] = 0;
    }
    
    // Second pass - count occurrences and revenue
    for (const log of logs.docs) {
      const dataLog = log.data();
      
      if (this.meetsEmployeeFilter(dataLog)) {
        totalTransactions++;
        
        // Get the vehicle type
        let vehicleType = dataLog.vehicle_type_custom_name || 'Unknown';
        
        // If this specific type wasn't in our first pass (shouldn't happen), use 'Other'
        if (!vehicleTypes.hasOwnProperty(vehicleType)) {
          vehicleType = 'Other';
        }
        
        // Update count
        vehicleTypes[vehicleType]++;
        
        // Calculate payment
        let amount = 0;
        
        // Check for amount_paid first (most direct payment field)
        if (dataLog.amount_paid !== undefined && dataLog.amount_paid !== null && !isNaN(parseFloat(dataLog.amount_paid))) {
          amount = parseFloat(dataLog.amount_paid);
        } 
        // If no amount_paid, try initial_payment + additional_payment
        else if ((dataLog.initial_payment !== undefined && dataLog.initial_payment !== null) || 
                 (dataLog.additional_payment !== undefined && dataLog.additional_payment !== null)) {
          const initialPayment = parseFloat(dataLog.initial_payment || 0);
          const additionalPayment = parseFloat(dataLog.additional_payment || 0);
          amount = initialPayment + additionalPayment;
        } 
        // If neither available, try total_payment
        else if (dataLog.total_payment !== undefined && dataLog.total_payment !== null && !isNaN(parseFloat(dataLog.total_payment))) {
          amount = parseFloat(dataLog.total_payment);
        }
        
        // Add to revenue
        vehicleRevenue[vehicleType] += amount;
        totalAmount += amount;
      }
    }
    
    // Convert to array format for display
    const vehicleData = Object.entries(vehicleTypes).map(([type, count]) => {
      const countNum = count as number;
      const revenue = vehicleRevenue[type] as number;
      const avgRevenuePerVehicle = countNum > 0 ? (revenue / countNum).toFixed(2) : '0.00';
      
      return {
        vehicle_type: type,
        count: countNum,
        percentage: totalTransactions > 0 ? ((countNum / totalTransactions) * 100).toFixed(2) + '%' : '0%',
        revenue: revenue,
        avg_revenue_per_vehicle: avgRevenuePerVehicle
      };
    });
    
    // Sort by count, high to low
    vehicleData.sort((a, b) => b.count - a.count);
    
    return {
      data: vehicleData,
      totalTransactions: totalTransactions,
      totalAmount: totalAmount,
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }

  /**
   * Process logs for vehicle type summary report
   */
  async processVehicleTypeSummaryReport(logs: any) {
    // Calculate summary metrics for all logs
    const summaryMetrics = this.calculateSummaryMetrics(logs.docs);

    const vehicleTypeData = {};
    let totalAmount = 0;
    
    // Process logs to create vehicle type summary
    for (const log of logs.docs) {
      const dataLog = log.data();
      
      // Get the date and vehicle type
      const date = moment(dataLog.time_in.seconds * 1000).format('MMM DD YYYY');
      let vehicleType = dataLog.vehicle_type_custom_name || 'Unknown';
      
      // Filter by employee if needed
      if (!this.meetsEmployeeFilter(dataLog)) {
        continue;
      }
      
      // Initialize date entry if it doesn't exist
      if (!vehicleTypeData[date]) {
        vehicleTypeData[date] = {
          date: date,
          headers: {
            values: []
          }
        };
      }
      
      // Calculate total payment from all possible payment fields
      let amount = 0;
      
      // Check for amount_paid first (most direct payment field)
      if (dataLog.amount_paid !== undefined && dataLog.amount_paid !== null && !isNaN(parseFloat(dataLog.amount_paid))) {
        amount = parseFloat(dataLog.amount_paid);
      } 
      // If no amount_paid, try initial_payment + additional_payment
      else if ((dataLog.initial_payment !== undefined && dataLog.initial_payment !== null) || 
               (dataLog.additional_payment !== undefined && dataLog.additional_payment !== null)) {
        const initialPayment = parseFloat(dataLog.initial_payment || 0);
        const additionalPayment = parseFloat(dataLog.additional_payment || 0);
        amount = initialPayment + additionalPayment;
      } 
      // If neither available, try total_payment
      else if (dataLog.total_payment !== undefined && dataLog.total_payment !== null && !isNaN(parseFloat(dataLog.total_payment))) {
        amount = parseFloat(dataLog.total_payment);
      }
      
      totalAmount += amount;
      
      // Find or create vehicle type entry
      let typeEntry = vehicleTypeData[date].headers.values.find(v => v.name === vehicleType);
      
      if (!typeEntry) {
        typeEntry = {
          name: vehicleType,
          count: 0,
          sales: 0
        };
        vehicleTypeData[date].headers.values.push(typeEntry);
      }
      
      // Update vehicle type stats
      typeEntry.count++;
      typeEntry.sales += amount;
    }
    
    // Convert to array
    const result = Object.values(vehicleTypeData);
    
    console.log('Processed vehicle type summary:', result);
    return {
      data: result,
      totalTransactions: summaryMetrics.totalTransactions,
      totalAmount: totalAmount, // Use the locally calculated amount from vehicle type summary logic
      totalRefundedAmount: summaryMetrics.totalRefundedAmount,
      totalCanceledCount: summaryMetrics.totalCanceledCount,
      totalUnpaidAmount: summaryMetrics.totalUnpaidAmount,
      totalUnpaidCount: summaryMetrics.totalUnpaidCount,
      netAmount: summaryMetrics.netAmount,
      totalNetAmount: summaryMetrics.totalNetAmount
    };
  }

  /**
   * Display cancellation details in a modal
   */
  showCancellationDetails(row: any) {
    if (!row) return;

    let contentHtml = '<div class="flex flex-row">';
    
    // Add image on the left if available
    if (row.cancellation_image_url) {
      contentHtml += `
        <div class="w-5/12 pr-4">
          <div id="imageLoading" class="my-3 text-center">
            <i class="fas fa-spinner fa-spin mr-2"></i> Loading image...
          </div>
          <img src="${row.cancellation_image_url}" class="w-full h-auto max-h-64 border rounded object-contain" 
          alt="Cancellation Image" 
          onload="document.getElementById('imageLoading').style.display='none';"
          onerror="this.onerror=null;this.src='assets/img/image-not-found.png';document.getElementById('imageLoading').style.display='none';">
        </div>
      `;
    }
    
    // Add details on the right
    contentHtml += '<div class="' + (row.cancellation_image_url ? 'w-7/12' : 'w-full') + ' text-left">';
    
    // Add cancellation details
    if (row.cancelled_at_formatted) {
      contentHtml += `<p class="mb-1 text-sm"><strong>Date Cancelled:</strong> ${row.cancelled_at_formatted}</p>`;
    }
    
    if (row.cancelled_by) {
      contentHtml += `<p class="mb-1 text-sm"><strong>Cancelled By:</strong> ${row.cancelled_by}</p>`;
    }
    
    if (row.cancellation_reason) {
      contentHtml += `<p class="mb-1 text-sm"><strong>Reason:</strong> ${row.cancellation_reason}</p>`;
    }
    
    // If it has refund data, also show that
    if (row.refund_amount) {
      contentHtml += '<hr class="my-2">';
      contentHtml += `<p class="mb-1 text-sm"><strong>Refund Amount:</strong> ₱${parseFloat(row.refund_amount).toFixed(2)}</p>`;
      
      if (row.refund_info) {
        contentHtml += `<p class="mb-1 text-sm"><strong>Refund Info:</strong> ${row.refund_info}</p>`;
      }
      
      if (row.refund_reason) {
        contentHtml += `<p class="mb-1 text-sm"><strong>Refund Reason:</strong> ${row.refund_reason}</p>`;
      }
      
      if (row.refunded_at_formatted) {
        contentHtml += `<p class="mb-1 text-sm"><strong>Date Refunded:</strong> ${row.refunded_at_formatted}</p>`;
      }
      
      if (row.refunded_by) {
        contentHtml += `<p class="mb-1 text-sm"><strong>Refunded By:</strong> ${row.refunded_by}</p>`;
      }
    }
    
    contentHtml += '</div></div>';
    
    Swal.fire({
      title: 'Cancellation Details',
      html: contentHtml,
      width: '550px',
      showCloseButton: true,
      showConfirmButton: false,
      focusConfirm: false
    });
  }
  
  /**
   * Display refund details in a modal
   */
  showRefundDetails(row: any) {
    if (!row) return;

    let contentHtml = '<div class="flex flex-row">';
    
    // Add image on the left if available
    if (row.refund_image_url) {
      contentHtml += `
        <div class="w-5/12 pr-4">
          <div id="refundImageLoading" class="my-3 text-center">
            <i class="fas fa-spinner fa-spin mr-2"></i> Loading image...
          </div>
          <img src="${row.refund_image_url}" class="w-full h-auto max-h-64 border rounded object-contain" 
          alt="Refund Image" 
          onload="document.getElementById('refundImageLoading').style.display='none';"
          onerror="this.onerror=null;this.src='assets/img/image-not-found.png';document.getElementById('refundImageLoading').style.display='none';">
        </div>
      `;
    }
    
    // Add details on the right
    contentHtml += '<div class="' + (row.refund_image_url ? 'w-7/12' : 'w-full') + ' text-left">';
    
    if (row.refund_amount) {
      contentHtml += `<p class="mb-1 text-sm"><strong>Refund Amount:</strong> ₱${parseFloat(row.refund_amount).toFixed(2)}</p>`;
    }
    
    if (row.refund_status) {
      contentHtml += `<p class="mb-1 text-sm"><strong>Status:</strong> ${row.refund_status}</p>`;
    }
    
    if (row.refund_info) {
      contentHtml += `<p class="mb-1 text-sm"><strong>Info:</strong> ${row.refund_info}</p>`;
    }
    
    if (row.refund_reason) {
      contentHtml += `<p class="mb-1 text-sm"><strong>Reason:</strong> ${row.refund_reason}</p>`;
    }
    
    if (row.refunded_at_formatted) {
      contentHtml += `<p class="mb-1 text-sm"><strong>Date Processed:</strong> ${row.refunded_at_formatted}</p>`;
    }
    
    if (row.refunded_by) {
      contentHtml += `<p class="mb-1 text-sm"><strong>Processed By:</strong> ${row.refunded_by}</p>`;
    }
    
    contentHtml += '</div></div>';
    
    Swal.fire({
      title: 'Refund Details',
      html: contentHtml,
      width: '550px',
      showCloseButton: true,
      showConfirmButton: false,
      focusConfirm: false
    });
  }

  formatAddress(address: any): string {
    if (!address) return 'Not Available';
    
    const parts = [];
    if (address.street_house_building_number) parts.push(address.street_house_building_number);
    if (address.city) parts.push(address.city);
    if (address.province) parts.push(address.province);
    
    return parts.join(', ') || 'Not Available';
  }

  /**
   * Export report data to JSON format as .txt file
   */
  async exportToJSON() {
    // Validate that we have data to export
    if (!this.tableData || this.tableData.length === 0) {
      Swal.fire({
        title: 'No Data',
        text: 'There is no data to export. Please generate a report first.',
        icon: 'warning'
      });
      return;
    }

    // Log export action
    await this.auditService.logExport(
      'json_report',
      `report_${new Date().toISOString().split('T')[0]}.txt`,
      this.tableData.length
    );

    try {
      // Show loading indicator
      Swal.fire({
        title: 'Generating JSON...',
        text: 'Please wait while we prepare your file.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Prepare data using existing method
      const exportData = this.prepareExportData();

      // Create comprehensive JSON structure
      const jsonData = {
        report_info: {
          report_type: this.selectedReportType,
          report_type_display: this.getReportDisplayName(),
          generated_date: new Date().toLocaleDateString(),
          generated_time: new Date().toLocaleTimeString(),
          period: this.reportPeriod,
          zone: this.selectedZone ? this.selectedZone.zone_name : 'All Zones'
        },
        summary: {
          total_transactions: this.totalTransactions,
          total_amount: this.totalAmount,
          total_canceled_count: this.totalCanceledCount,
          total_cancelled_amount: this.totalCancelledAmount,
          total_unpaid_count: this.totalUnpaidCount,
          total_unpaid_amount: this.totalUnpaidAmount,
          total_refunded_amount: this.totalRefundedAmount,
          total_net_amount: this.totalNetAmount
        },
        table_headers: this.tableHeaders,
        data: exportData
      };

      // Convert to JSON string with pretty formatting
      const jsonString = JSON.stringify(jsonData, null, 2);

      // Generate file name
      const fileName = `${this.selectedReportType}_report_${new Date().toISOString().slice(0,10)}.txt`;

      // Create and download file
      const blob = new Blob([jsonString], { type: 'text/plain;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show success message
      Swal.fire({
        title: 'Success',
        text: 'JSON file has been downloaded successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error exporting to JSON:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to export to JSON. Please try again.',
        icon: 'error'
      });
    }
  }

  /**
   * Get display name for current report type
   */
  private getReportDisplayName(): string {
    const reportNames: { [key: string]: string } = {
      'general': 'General Report',
      'unpaid': 'Unpaid Transactions Summary',
      'voided': 'Voided/Refunded Transactions',
      'status': 'Transaction Status Breakdown',
      'payment': 'Revenue by Payment Type',
      'discount': 'Discount Utilization Report',
      'duration': 'Parking Duration Distribution',
      'employee': 'Employee Performance Report',
      'trend': 'Transaction Trend (Per Day)',
      'assignment': 'Manual vs Automatic Assignment',
      'dsa': 'Daily Summary per Attendant'
    };
    return reportNames[this.selectedReportType] || this.selectedReportType;
  }

}
